// ✅ Environment Variables Configuration
export const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.crmaarogyaindia.com/api';

// Origin without the /api suffix — media/asset URLs ke liye
export const ASSET_BASE_URL = BASE_URL.replace(/\/api\/?$/, '');
const AUTH_TOKEN_KEY = process.env.REACT_APP_AUTH_TOKEN_KEY || 'authToken';
const USER_KEY = process.env.REACT_APP_USER_KEY || 'user';

// ============================================================
// 🔄 SILENT TOKEN REFRESH SYSTEM
// ============================================================

let isRefreshing = false;           // Ek time pe sirf ek refresh call
let refreshSubscribers = [];        // Pending requests queue

// Jab token refresh ho jaye, saari pending requests ko naya token do
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

// Pending request queue mein add karo
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// Refresh token se naya access token lo
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Refresh token expired or invalid');
  }

  const data = await response.json();

  // ✅ Naya token store karo (backend jo bhi field name return kare)
  const newAccessToken = data.accessToken || data.token || data.authToken;
  const newRefreshToken = data.refreshToken;

  if (!newAccessToken) {
    throw new Error('No access token in refresh response');
  }

  localStorage.setItem('authToken', newAccessToken);
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken);
  }

  return newAccessToken;
};

// ✅ Logout helper — centralized
const performLogout = async () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.id) {
      await sessionApi.markOffline(user.id);
      console.log('✅ Mark Offline successful');
    }
  } catch (err) {
    console.error('markOffline failed:', err);
  } finally {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('activeView');
    // Agle login par pichhle user ki permissions leak na hon
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('allPermissions');
    window.location.href = '/login';
  }
};

// ============================================================
// Auth Helper Functions
// ============================================================
export const authHelpers = {
  getCurrentUser: () => {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        console.error('Error parsing user:', e);
        return null;
      }
    }
    return null;
  },

  getAuthToken: () => {
    return localStorage.getItem('authToken');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },

  hasRole: (role) => {
    const user = authHelpers.getCurrentUser();
    return user && user.role === role;
  },

  isAdmin: () => {
    return authHelpers.hasRole('ADMIN');
  },

  getUserId: () => {
    const user = authHelpers.getCurrentUser();
    return user ? user.id : null;
  },

  getUserName: () => {
    const user = authHelpers.getCurrentUser();
    return user ? user.name : null;
  },

  getUserEmail: () => {
    const user = authHelpers.getCurrentUser();
    return user ? user.email : null;
  },

  // ✅ Login ke time refreshToken bhi save karo
  saveTokens: (accessToken, refreshToken, user) => {
    localStorage.setItem('authToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  logout: () => {
    performLogout();
  }
};

// Helper function to get user info (internal use)
const getUserInfo = () => {
  try {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error parsing user info:', error);
    return null;
  }
};

// ============================================================
// ✅ UPDATED handleResponse — with silent refresh
// ============================================================
const handleResponse = async (response, originalRequestFn = null) => {
  // 401 aaya — token refresh try karo
  if (response.status === 401) {
    console.warn('⚠️ 401 Unauthorized caught in handleResponse', { url: response.url, hasRetryFn: !!originalRequestFn });

    // Retry function nahi hai (e.g. handleDirectFetch se aaya response jise
    // pehle hi retry kiya ja chuka hai) — dobara call karne par TypeError aata tha.
    if (typeof originalRequestFn !== 'function') {
      throw new Error('Session expired. Please login again.');
    }

    // Agar already refresh ho raha hai — queue mein wait karo
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        addRefreshSubscriber(async (newToken) => {
          try {
            const retryResponse = await originalRequestFn(newToken);
            resolve(await handleResponse(retryResponse)); // Use safe parsing
          } catch (err) {
            reject(err);
          }
        });
      });
    }

    // Refresh start karo
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      onRefreshed(newToken); // Saari queued requests ko naya token do

      // Original request retry karo naye token se
      const retryResponse = await originalRequestFn(newToken);
      return handleResponse(retryResponse); // Use safe parsing without passing originalRequestFn to avoid loop

    } catch (refreshError) {
      isRefreshing = false;
      refreshSubscribers = [];
      console.error('Token refresh failed:', refreshError);

      throw new Error('Session expired. Please login again.');
    }
  }

  // Handle based on status code
  if (response.status === 204) return { success: true };

  // Parse body safely
  const contentType = response.headers.get('content-type');
  let data;
  try {
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
  } catch (err) {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      (data && typeof data === 'object' ? (data.message || data.error || data.msg) : data) ||
      `Request failed with status ${response.status}`
    );
    error.response = response;
    error.data = data;
    error.status = response.status;
    console.error(`❌ API Error (${response.status}):`, data);
    throw error;
  }

  return data;
};

// ============================================================
// ✅ UPDATED fetchApi — originalRequestFn pass karta hai
// ============================================================
const fetchApi = async (endpoint, options = {}) => {
  const url = `${BASE_URL}${endpoint}`;
  const authToken = localStorage.getItem(AUTH_TOKEN_KEY);

  const buildConfig = (token) => {
    const { headers: optHeaders, ...restOptions } = options;
    return {
      method: 'GET',        // default
      ...restOptions,       // overrides method if caller passed one
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...optHeaders,      // caller's extra headers last
      },
      mode: 'cors',
    };
  };

  // Original request function — retry ke liye use hogi
  const makeRequest = (token) => fetch(url, buildConfig(token));

  try {
    const response = await makeRequest(authToken);

    // handleResponse ko originalRequestFn pass karo for retry
    return handleResponse(response, makeRequest);

  } catch (error) {
    // Agar handleResponse ne throw kiya toh re-throw karo
    if (error.message === 'Session expired. Please login again.') {
      throw error;
    }
    console.error('Fetch error:', error);
    if (error.message === 'Failed to fetch') {
      throw new Error(`Cannot connect to server at ${BASE_URL}. Please check if the server is running and CORS is enabled.`);
    }
    throw error;
  }
};

// ============================================================
// ✅ Helper for direct fetch calls (file uploads, blobs etc.)
// ============================================================
const handleDirectFetch = async (url, options = {}) => {
  const authToken = localStorage.getItem('authToken');

  const buildConfig = (token) => ({
    ...options,
    headers: {
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  });

  const makeRequest = (token) => fetch(url, buildConfig(token));

  let response = await makeRequest(authToken);

  // 401 — refresh try karo
  if (response.status === 401) {
    if (isRefreshing) {
      // Queue mein wait karo
      return new Promise((resolve, reject) => {
        addRefreshSubscriber(async (newToken) => {
          try {
            resolve(await makeRequest(newToken));
          } catch (err) {
            reject(err);
          }
        });
      });
    }

    isRefreshing = true;
    try {
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      onRefreshed(newToken);
      response = await makeRequest(newToken); // Retry
    } catch (refreshError) {
      isRefreshing = false;
      refreshSubscribers = [];

      throw new Error('Session expired. Please login again.');
    }
  }

  return response; // Raw response return karo (blob/json caller decide karega)
};

// ============================================================
// Lead API
// ============================================================
export const leadAPIApi = {
  getAllLeadAPIs: () => fetchApi('/lead-api'),
  getLeadAPIById: (id) => fetchApi(`/lead-api/${id}`),
  createLeadAPI: (data) => fetchApi('/lead-api', { method: 'POST', body: JSON.stringify(data) }),
  updateLeadAPI: (id, data) => fetchApi(`/lead-api/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLeadAPI: (id) => fetchApi(`/lead-api/${id}`, { method: 'DELETE' }),
  getAllCampaigns: () => fetchApi('/order-campaign'),
  getIntegrationGuide: (id) => fetchApi(`/leads/integration-guide/${id}`),
  downloadIntegrationGuide: (id) => handleDirectFetch(`${BASE_URL}/leads/integration-guide/${id}`),
};
export const fetchApiDirect = (endpoint, options = {}) => fetchApi(endpoint, options);
// Company Profile API
export const companyProfileApi = {
  getAllProfiles: () => fetchApi('/company-profile'),
  getActiveProfiles: () => fetchApi('/company-profile/active'),
  getDefaultProfile: () => fetchApi('/company-profile/default'),
  getProfileById: (id) => fetchApi(`/company-profile/${id}`),
  createCompanyProfile: (data) => fetchApi('/company-profile', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateProfile: (id, data) => fetchApi(`/company-profile/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteProfile: (id) => fetchApi(`/company-profile/${id}`, {
    method: 'DELETE',
  }),
  toggleStatus: (id) => fetchApi(`/company-profile/${id}/toggle-status`, {
    method: 'PATCH',
  }),
  setactive: (id) => fetchApi(`/company-profile/${id}/set-active`, {
    method: 'PATCH',
  }),

  uploadLogo: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/company-profile/${id}/upload-logo`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  uploadSignature: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/company-profile/${id}/upload-signature`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  uploadUpiScanner: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/company-profile/${id}/upload-upi-scanner`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },
};

// Invoice API
export const invoiceApi = {
  getAllInvoices: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    if (params.search) q.append('search', params.search);
    const qs = q.toString();
    try {
      return await fetchApi(`/invoice${qs ? '?' + qs : ''}`);
    } catch (err) {
      if (err.status === 404) {
        return await fetchApi(`/invoice/billing-list${qs ? '?' + qs : ''}`);
      }
      throw err;
    }
  },
  getBillingList: () => fetchApi('/invoice/billing-list'),
  getInvoiceById: (id) => fetchApi(`/invoice/${id}`),
  getInvoiceByNumber: (invoiceNumber) => fetchApi(`/invoice/number/${encodeURIComponent(invoiceNumber)}`),
  getInvoiceByOrderId: async (orderId) => {
    try {
      return await fetchApi(`/invoice/order/${orderId}`);
    } catch (err) {
      if (err.status === 404) {
        return await fetchApi(`/invoice/${orderId}`);
      }
      throw err;
    }
  },
  getInvoicesByStatus: (status) => fetchApi(`/invoice/status/${status}`),
  searchInvoices: (query) => fetchApi(`/invoice/search?query=${encodeURIComponent(query)}`),
  createInvoiceFromOrder: async (orderId, companyProfileId) => {
    const qs = companyProfileId ? `?companyProfileId=${companyProfileId}` : '';
    try {
      return await fetchApi(`/invoice/from-order/${orderId}${qs}`, {
        method: 'POST',
      });
    } catch (err) {
      if (companyProfileId) {
        try {
          return await fetchApi(`/invoice/from-order/${orderId}`, { method: 'POST' });
        } catch (_) {}
      }
      throw err;
    }
  },
  createInvoice: (data) => fetchApi('/invoice', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateInvoice: (id, data) => fetchApi(`/invoice/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updateInvoiceStatus: (id, status) => fetchApi(`/invoice/${id}/status?status=${status}`, {
    method: 'PATCH',
  }),
  deleteInvoice: (id) => fetchApi(`/invoice/${id}`, {
    method: 'DELETE',
  }),
  // ✅ Single invoice download by order ID / invoice ID with fallback endpoints
  downloadByOrderId: async (orderId) => {
    const endpoints = [
      `${BASE_URL}/invoice/order/${orderId}/download`,
      `${BASE_URL}/invoice/${orderId}/download`
    ];

    let lastError = null;
    for (const url of endpoints) {
      try {
        const response = await handleDirectFetch(url, { method: 'GET' });
        if (response.ok) {
          return await response.blob();
        }
        const errorData = await response.json().catch(() => ({}));
        const errMsg = errorData.message || errorData.error || `Download failed: ${response.status}`;
        lastError = new Error(errMsg);
        if (response.status !== 404) {
          throw lastError;
        }
      } catch (err) {
        lastError = err;
        if (err.message && !err.message.includes('404')) {
          throw err;
        }
      }
    }
    throw lastError || new Error(`Invoice download endpoint not found for ID: ${orderId}`);
  },
};

// ============================================================
// targetsApi — End-to-End Target Backend Integration
// ============================================================
export const targetsApi = {

  // ─── 1. GET Assignees (Cards grid populate) ─────────────────
  getAssignees: (targetType, assigneeType, floorId = null, teamId = null) => {
    const q = new URLSearchParams({ targetType, assigneeType });
    if (floorId) q.append('floorId', floorId);
    const mappedTeamId = String(teamId) === '7' ? '4' : teamId;
    if (mappedTeamId) q.append('teamId', mappedTeamId);
    return fetchApiDirect(`/targets/assignees?${q.toString()}`);
  },

  // ─── 2. POST Preview (Assignment summary footer) ────────────
  previewTarget: (payload) =>
    fetchApiDirect('/targets/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ─── 3. POST Assign (Simple bulk assign) ─────────────────────
  assignTarget: (payload) =>
    fetchApiDirect('/targets/assign', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ─── 4. POST Assign Chain (Floor + TL + Employee ek saath) ───
  assignChain: (payload) =>
    fetchApiDirect('/targets/assign/chain', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ─── 5. GET Floor Budget (TL assign se pehle check) ─────────
  getFloorBudget: (floorId, targetType = 'ORDERS') =>
    fetchApiDirect(`/targets/floor/${floorId}/budget?targetType=${targetType}`),

  // ─── 6. GET TL Budget (Employee assign se pehle check) ───────
  getTeamLeadBudget: (tlId, targetType = 'ORDERS') =>
    fetchApiDirect(`/targets/team-lead/${tlId}/budget?targetType=${targetType}`),

  // ─── 7. GET Current Target (per-assignee badge) ──────────────
  getCurrentTarget: (entityType, entityId, targetType = 'ORDERS') =>
    fetchApiDirect(`/targets/${entityType}/${entityId}?targetType=${targetType}`),

  // ─── 8. GET History ──────────────────────────────────────────
  getEmployeeHistory: (employeeId) =>
    fetchApiDirect(`/targets/employee/${employeeId}/history`),

  getFloorHistory: (floorId) =>
    fetchApiDirect(`/targets/floor/${floorId}/history`),

  // ─── 9. DELETE (Deactivate Target) ───────────────────────────
  deleteTarget: (targetId) =>
    fetchApiDirect(`/targets/${targetId}`, { method: 'DELETE' }),
};

// ═════════════════════════════════════════════════════════════
//  NDR API — NDR Orders list, Assign, Status updates
// ═════════════════════════════════════════════════════════════
export const ndrApi = {
  // NDR list — full filter support
  getNDROrders: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.searchTerm)       qp.append('searchTerm', params.searchTerm);
    if (params.courier)          qp.append('courier', params.courier);
    if (params.courierPartner)   qp.append('courierPartner', params.courierPartner);
    if (params.status)           qp.append('status', params.status);
    if (params.dateRangePreset)  qp.append('dateRangePreset', params.dateRangePreset);
    if (params.startDate)        qp.append('startDate', params.startDate);
    if (params.endDate)          qp.append('endDate', params.endDate);
    if (params.employeeId)       qp.append('employeeId', params.employeeId);
    if (params.category)         qp.append('category', params.category);
    if (params.paymentMode)      qp.append('paymentMode', params.paymentMode);
    if (params.ndrById)          qp.append('ndrById', params.ndrById);
    if (params.campaign)         qp.append('campaign', params.campaign);
    if (params.customerStatus)   qp.append('customerStatus', params.customerStatus);
    if (params.orderType)        qp.append('orderType', params.orderType);
    if (params.page !== undefined) qp.append('page', params.page);
    if (params.size !== undefined) qp.append('size', params.size);

    const qs = qp.toString();
    return fetchApi(`/orders/ndr${qs ? '?' + qs : ''}`);
  },

  getAssignableEmployees: (teamId) => {
    const qs = teamId ? `?teamId=${teamId}` : '';
    return fetchApi(`/orders/ndr/employees/assignable${qs}`);
  },

  assignNDROrders: (orderIds, assignedToEmployeeId) => {
    return fetchApi('/orders/ndr/assign', {
      method: 'POST',
      body: JSON.stringify({ orderIds, assignedToEmployeeId }),
    });
  },

  updateCustomerStatus: (orderId, statusUpdate) => {
    return fetchApi(`/orders/ndr/${orderId}/customer-status`, {
      method: 'PATCH',
      body: JSON.stringify(statusUpdate),
    });
  },

  bulkStatusUpdate: (orderIds, status, notes) => {
    return fetchApi('/orders/ndr/bulk-status-update', {
      method: 'POST',
      body: JSON.stringify({ orderIds, status, notes }),
    });
  },

  convertToRTO: (orderId, reason) => {
    return fetchApi(`/orders/ndr/${orderId}/convert-to-rto`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  bulkConvertToRTO: (orderIds, conversionReason) => {
    return fetchApi('/orders/ndr/bulk-convert-to-rto', {
      method: 'POST',
      body: JSON.stringify({ orderIds, conversionReason }),
    });
  },
};

// Orders API
export const ordersApi = {
  getNDRDashboardSummary: () => fetchApi('/orders/ndr/dashboard-summary'),
  getOFDDashboardSummary: () => fetchApi('/workflow/ofd/dashboard-summary'),

  getByPincode: (pincode) => fetchApi(`/pincode/${pincode}`),
  reassignOrder: (orderId, data) => fetchApi(`/orders/${orderId}/reassign`, {
    method: 'PATCH',
    body: JSON.stringify(data), // { newAssignedToId, reason? }
  }),
  bulkReassignOrders: (data) => fetchApi('/orders/bulk/reassign', {
    method: 'PATCH',
    body: JSON.stringify(data), // { orderIds, newAssignedToId, reason? }
  }),
  // ✅ Filter dropdown options — Agents, Team Leads, Categories, Payment Modes, Campaigns
  getFilterOptions: () => fetchApi('/workflow/filter-options'),
  exportOrdersExcel: async (query = '') => {
    const response = await handleDirectFetch(
      `${BASE_URL}/orders/export/excel${query}`,
      { method: 'GET' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Export failed: ${response.status}`);
    }
    return response.blob();
  },
  // ─── Excel Bulk Status ────────────────────────────────────────────────────────
  downloadBulkStatusTemplate: async () => {
    const response = await handleDirectFetch(
      `${BASE_URL}/workflow/bulk/excel-status/template`,
      { method: 'GET' }
    );
    if (!response.ok) throw new Error(`Failed to download template: ${response.status}`);
    return response.blob();
  },

  validateBulkStatusExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(
      `${BASE_URL}/workflow/bulk/excel-status/validate`,
      { method: 'POST', body: formData }
    );
    return handleResponse(response);
  },

  uploadBulkStatusExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(
      `${BASE_URL}/workflow/bulk/excel-status`,
      { method: 'POST', body: formData }
    );
    return handleResponse(response);
  },
  smartSearch: (params) =>
    fetchApi(`/orders/smart-search?${new URLSearchParams(params)}`),

  getOrdersByUserAndStatus: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders/filter?${queryString}` : '/orders';
    return fetchApi(endpoint);
  },

  // ✅ Dispatched tab
  getDispatched: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.dateType) queryParams.append('dateType', params.dateType);
    if (params.dateRangePreset) queryParams.append('dateRangePreset', params.dateRangePreset);
    if (params.category) queryParams.append('category', params.category);
    if (params.paymentMode) queryParams.append('paymentMode', params.paymentMode);
    if (params.campaign) {
      queryParams.append('campaign', params.campaign);
      queryParams.append('campaignName', params.campaign);
    } else if (params.campaignName) {
      queryParams.append('campaign', params.campaignName);
      queryParams.append('campaignName', params.campaignName);
    }
    if (params.orderBy) {
      queryParams.append('orderBy', params.orderBy);
      queryParams.append('employeeId', params.orderBy);
    } else if (params.employeeId) {
      queryParams.append('orderBy', params.employeeId);
      queryParams.append('employeeId', params.employeeId);
    }
    if (params.actionBy) queryParams.append('actionBy', params.actionBy);
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size !== undefined) queryParams.append('size', params.size);
    if (params.limit) queryParams.append('limit', params.limit);

    const queryString = queryParams.toString();
    return fetchApi(`/workflow/dispatched${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  },

  getDispatchOrders: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/workflow/dispatch?${queryString}` : '/workflow/dispatch';
    return fetchApi(endpoint);
  },

  // ✅ UPDATED — handleDirectFetch use karo
  generateBulkLabels: async (orderIds) => {
    try {
      const response = await handleDirectFetch(`${BASE_URL}/workflow/labels/download/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: Failed to generate labels`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error generating bulk labels:', error);
      throw error;
    }
  },

  generateBulkDeliveredLabels: async (orderIds) => {
    try {
      const response = await handleDirectFetch(`${BASE_URL}/workflow/labels/redownload/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: Failed to generate shipped labels`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error generating shipped bulk labels:', error);
      throw error;
    }
  },

  increaseOrderAmount: (orderId, data) => {
    return fetchApi(`/orders/${orderId}/increase-amount`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  dispatchOrder: (orderId, data) => {
    return fetchApi(`/workflow/dispatch/${orderId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  // ✅ Ready to ship tab
  getReadyToShipOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.dateType) queryParams.append('dateType', params.dateType);
    if (params.dateRangePreset) queryParams.append('dateRangePreset', params.dateRangePreset);
    if (params.category) queryParams.append('category', params.category);
    if (params.paymentMode) queryParams.append('paymentMode', params.paymentMode);
    if (params.campaign) {
      queryParams.append('campaign', params.campaign);
      queryParams.append('campaignName', params.campaign);
    } else if (params.campaignName) {
      queryParams.append('campaign', params.campaignName);
      queryParams.append('campaignName', params.campaignName);
    }
    if (params.orderBy) {
      queryParams.append('orderBy', params.orderBy);
      queryParams.append('employeeId', params.orderBy);
    } else if (params.employeeId) {
      queryParams.append('orderBy', params.employeeId);
      queryParams.append('employeeId', params.employeeId);
    }
    if (params.actionBy) queryParams.append('actionBy', params.actionBy);
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size !== undefined) queryParams.append('size', params.size);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return fetchApi(`/workflow/ready-to-ship${qs ? '?' + qs : ''}`);
  },
  assignOFDOrders: async (data) => {
    try {
      return await fetchApi('/workflow/ofd/assign', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (err) {
      console.warn('Primary /workflow/ofd/assign failed, attempting fallback to /orders/ndr/assign:', err);
      return await fetchApi('/orders/ndr/assign', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    }
  },
  bulkReadyToShip: (orderIds) => {
    return fetchApi('/workflow/dispatch/bulk/ready-to-ship', {
      method: 'POST',
      body: JSON.stringify({ orderIds }),
    });
  },

  bulkMarkShipped: (orderIds) => {
    return fetchApi('/workflow/dispatch/bulk/shipped', {
      method: 'POST',
      body: JSON.stringify({ orderIds }),
    });
  },

  getlabelpending: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/workflow/dispatch?${queryString}` : '/workflow/label-pending';
    return fetchApi(endpoint);
  },

  // ✅ UPDATED — handleDirectFetch use karo
  generateOrderLabels: async (data) => {
    try {
      const response = await handleDirectFetch(`${BASE_URL}/workflow/labels/generate/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: `Request failed with status ${response.status}`
        }));
        throw error;
      }

      return await response.blob();
    } catch (error) {
      console.error('Error generating order labels:', error);
      throw error;
    }
  },

  Orderlabelsgenerate: (orderId, data) => {
    return fetchApi(`/workflow/labels/generate/${orderId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  verifyOrder: (orderId) => {
    return fetchApi(`/workflow/verified/${orderId}/verify`, {
      method: 'POST',
    });
  },

  holdOrder: (orderId, data) => {
    return fetchApi(`/workflow/verified/${orderId}/hold`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getOrderbyuser: (orderId) => {
    return fetchApi(`/orders/user/${orderId}`);
  },
  getAllOrder: () => {
    return fetchApi(`/orders?page=0&size=10000`);
  },

  markAsPending: (orderId, data) => {
    return fetchApi(`/workflow/verified/${orderId}/pending`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getOrderTracking: (orderId) => {
    return fetchApi(`/orders/workflow/${orderId}/tracking`);
  },

  markAsOFD: (orderId) => {
    return fetchApi(`/orders/workflow/${orderId}/mark-ofd`, {
      method: 'POST',
    });
  },

  markAsDelivered: (orderId, data) => {
    return fetchApi(`/orders/workflow/${orderId}/mark-delivered`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCourierDetails: (orderId, data) => {
    return fetchApi(`/orders/workflow/${orderId}/courier`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  cancelOrder: (orderId) => {
    return fetchApi(`/orders/workflow/${orderId}/cancel`, {
      method: 'POST',
    });
  },

  bulkDispatch: (data) => {
    return fetchApi('/orders/workflow/bulk-dispatch', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getInTransitOrders: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/workflow/in-transit?${queryString}` : '/workflow/in-transit';
    return fetchApi(endpoint);
  },

  // ✅ Shipped tab
  getShipped: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.dateType) queryParams.append('dateType', params.dateType);
    if (params.dateRangePreset) queryParams.append('dateRangePreset', params.dateRangePreset);
    if (params.category) queryParams.append('category', params.category);
    if (params.paymentMode) queryParams.append('paymentMode', params.paymentMode);
    if (params.campaign) {
      queryParams.append('campaign', params.campaign);
      queryParams.append('campaignName', params.campaign);
    } else if (params.campaignName) {
      queryParams.append('campaign', params.campaignName);
      queryParams.append('campaignName', params.campaignName);
    }
    if (params.orderBy) {
      queryParams.append('orderBy', params.orderBy);
      queryParams.append('employeeId', params.orderBy);
    } else if (params.employeeId) {
      queryParams.append('orderBy', params.employeeId);
      queryParams.append('employeeId', params.employeeId);
    }
    if (params.actionBy) queryParams.append('actionBy', params.actionBy);
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size !== undefined) queryParams.append('size', params.size);
    if (params.limit) queryParams.append('limit', params.limit);

    const queryString = queryParams.toString();
    return fetchApi(`/workflow/shipped${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
  },

  getInTransitByCourier: (courier) => {
    return fetchApi(`/workflow/in-transit?courier=${encodeURIComponent(courier)}`);
  },

  getInTransitByStatus: (status) => {
    return fetchApi(`/workflow/in-transit?status=${encodeURIComponent(status)}`);
  },

  getInTransitByEmployee: (employeeId) => {
    return fetchApi(`/workflow/in-transit?employeeId=${encodeURIComponent(employeeId)}`);
  },

  getOFDOrders: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/workflow/ofd?${queryString}` : '/workflow/ofd';
    return fetchApi(endpoint);
  },

  getOFDTabCounts: () => fetchApi('/workflow/ofd/tab-counts'),

  updateOrder: (orderId, data) => {
    return fetchApi(`/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateOFDCustomerStatus: (orderId, data) => {
    return fetchApi(`/workflow/ofd/${orderId}/customer-status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  markOFDAsDelivered: (orderId, data) => {
    return fetchApi(`/workflow/ofd/${orderId}/deliver`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getDeliveredOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.employeeId) queryParams.append('agentId', params.employeeId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.category) queryParams.append('category', params.category);
    if (params.paymentMode) queryParams.append('paymentMode', params.paymentMode);
    if (params.teamLeadId) queryParams.append('teamLeadId', params.teamLeadId);
    if (params.campaign) queryParams.append('campaign', params.campaign);
    if (params.page !== undefined) queryParams.append('page', params.page);  // ✅
    if (params.size !== undefined) queryParams.append('size', params.size);  // ✅

    const queryString = queryParams.toString();
    return fetchApi(`/workflow/delivered${queryString ? '?' + queryString : ''}`);
  },

  getNDROrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.status) queryParams.append('status', params.status);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.courierPartner && params.courierPartner !== 'All Partners')
      queryParams.append('courierPartner', params.courierPartner);
    if (params.ordersType) queryParams.append('ordersType', params.ordersType);
    if (params.orderType) queryParams.append('orderType', params.orderType);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.dateRangePreset) queryParams.append('dateRangePreset', params.dateRangePreset);
    if (params.date && !params.startDate) queryParams.append('date', params.date);
    if (params.category) queryParams.append('category', params.category);
    if (params.paymentMode) queryParams.append('paymentMode', params.paymentMode);
    if (params.ndrBy) queryParams.append('ndrBy', params.ndrBy);
    if (params.campaign) queryParams.append('campaign', params.campaign);
    if (params.customerStatus) queryParams.append('customerStatus', params.customerStatus);
    if (params.agent) queryParams.append('agent', params.agent);
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size !== undefined) queryParams.append('size', params.size);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/ndr${queryString ? '?' + queryString : ''}`);
  },

  getNDRRequests: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.courierPartner) queryParams.append('courierPartner', params.courierPartner);
    if (params.ordersType) queryParams.append('ordersType', params.ordersType);
    if (params.status) queryParams.append('status', params.status);
    if (params.customerStatus) queryParams.append('customerStatus', params.customerStatus);
    if (params.date) queryParams.append('date', params.date);
    if (params.category) queryParams.append('category', params.category);
    if (params.paymentMode) queryParams.append('paymentMode', params.paymentMode);
    if (params.ndrBy) queryParams.append('ndrBy', params.ndrBy);
    if (params.campaign) queryParams.append('campaign', params.campaign);
    if (params.agent) queryParams.append('agent', params.agent);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/ndr/requests${queryString ? '?' + queryString : ''}`);
  },

  getNDRDetail: (orderId) => fetchApi(`/orders/ndr/${orderId}/detail`),

  updateNDRCustomerStatus: (orderId, data) => {
    return fetchApi(`/orders/ndr/${orderId}/customer-status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  convertNDRToRTO: (orderId, data) => {
    return fetchApi(`/orders/ndr/${orderId}/convert-to-rto`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  bulkConvertNDRToRTO: (data) => {
    return fetchApi('/orders/ndr/bulk-convert-to-rto', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  assignNDROrders: (data) => {
    return fetchApi('/orders/ndr/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  assignNDRToCourier: (data) => {
    return fetchApi('/orders/ndr/assign-courier', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  resendNDRNotification: (orderId, channel = 'whatsapp') => {
    return fetchApi(`/orders/ndr/${orderId}/resend-notification?channel=${encodeURIComponent(channel)}`, {
      method: 'POST',
    });
  },

  getNDRStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders/ndr/stats?${queryString}` : '/orders/ndr/stats';
    return fetchApi(endpoint);
  },

  getNDRResolutionSummary: (employeeId = null) => {
    const queryString = employeeId ? `?employeeId=${employeeId}` : '';
    return fetchApi(`/orders/ndr/resolution-summary${queryString}`);
  },

  getNDRAttempts: (orderId) => fetchApi(`/orders/ndr/${orderId}/attempts`),

  getNDREmployees: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.teamId) queryParams.append('teamId', params.teamId);
    if (params.departmentId) queryParams.append('departmentId', params.departmentId);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/ndr/employees/assignable${queryString ? '?' + queryString : ''}`);
  },

  getNDRAssignableEmployees: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.teamId) queryParams.append('teamId', params.teamId);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/ndr/employees/assignable${queryString ? '?' + queryString : ''}`);
  },

  convertToRTO: (orderId, reason = null) => {
    const body = reason ? JSON.stringify({ reason }) : null;
    return fetchApi(`/orders/ndr/${orderId}/convert-to-rto`, {
      method: 'POST',
      body: body,
    });
  },

  logNDRAttempt: (orderId, data) => {
    return fetchApi(`/orders/ndr/${orderId}/log-attempt`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  scheduleNDRFollowup: (orderId, data) => {
    return fetchApi(`/orders/ndr/${orderId}/schedule-followup`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getNDRStatistics: (params = {}) => {
    const { employeeId, startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/ndr/stats${queryString ? '?' + queryString : ''}`);
  },

  bulkNDRStatusUpdate: (bulkUpdate) => {
    return fetchApi('/orders/ndr/bulk-status-update', {
      method: 'POST',
      body: JSON.stringify(bulkUpdate),
    });
  },

  bulkUpdateNDRStatus: (data) => {
    return fetchApi('/orders/ndr/bulk-status-update', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getDecreaseRequests: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders/decrease-requests?${queryString}` : '/orders/decrease-requests';
    return fetchApi(endpoint);
  },

  updateDecreaseRequestStatus: (requestId, status, notes = '') => {
    return fetchApi(`/orders/decrease-requests/${requestId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  },

  getRTOOrders: (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.courier) queryParams.append('courier', params.courier);
    if (params.courierPartner && params.courierPartner !== 'All Partners')
      queryParams.append('courierPartner', params.courierPartner);
    if (params.date) queryParams.append('date', params.date);
    if (params.dateType && params.dateType !== 'Select Type')
      queryParams.append('dateType', params.dateType);
    if (params.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params.teamId) queryParams.append('teamId', params.teamId);
    if (params.category && params.category !== 'All Categories')
      queryParams.append('category', params.category);
    if (params.status && params.status !== 'All status')
      queryParams.append('status', params.status);
    if (params.paymentMode && params.paymentMode !== 'All Modes')
      queryParams.append('paymentMode', params.paymentMode);
    if (params.campaign && params.campaign !== 'All Campaigns')
      queryParams.append('campaign', params.campaign);
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size !== undefined) queryParams.append('size', params.size);

    const queryString = queryParams.toString();
    return fetchApi(`/orders/rto/filter${queryString ? '?' + queryString : ''}`);
  },

  getRTOOrdersByStatus: (status, employeeId = null) => {
    const queryParams = new URLSearchParams();
    queryParams.append('status', status);
    if (employeeId) queryParams.append('employeeId', employeeId);
    return fetchApi(`/orders/rto/by-status?${queryParams.toString()}`);
  },

  getRemainingRTOOrders: (params = {}) => {
    const { courier, employeeId } = params;
    const queryParams = new URLSearchParams();
    if (courier) queryParams.append('courier', courier);
    if (employeeId) queryParams.append('employeeId', employeeId);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/rto/remaining${queryString ? '?' + queryString : ''}`);
  },

  getRTODetail: (orderId) => fetchApi(`/orders/rto/${orderId}`),

  initiateRTO: (orderId, initiationDetails = null) => {
    return fetchApi(`/orders/rto/${orderId}/initiate`, {
      method: 'POST',
      body: initiationDetails ? JSON.stringify(initiationDetails) : null,
    });
  },

  updateRTOStatus: (orderId, statusUpdate) => {
    return fetchApi(`/orders/rto/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(statusUpdate),
    });
  },

  markRTOInTransit: (orderId, trackingInfo = null) => {
    return fetchApi(`/orders/rto/${orderId}/mark-in-transit`, {
      method: 'POST',
      body: trackingInfo ? JSON.stringify(trackingInfo) : null,
    });
  },

  markRTODelivered: (orderId, confirmation = null) => {
    return fetchApi(`/orders/rto/${orderId}/mark-delivered`, {
      method: 'POST',
      body: confirmation ? JSON.stringify(confirmation) : null,
    });
  },

  updateRTOTracking: (orderId, trackingUpdate) => {
    return fetchApi(`/orders/rto/${orderId}/tracking`, {
      method: 'PATCH',
      body: JSON.stringify(trackingUpdate),
    });
  },

  getRTOTrackingHistory: (orderId) => fetchApi(`/orders/rto/${orderId}/tracking-history`),

  updateRTODeliveryDate: (orderId, deliveryDate) => {
    return fetchApi(`/orders/rto/${orderId}/delivery-date`, {
      method: 'PATCH',
      body: JSON.stringify(deliveryDate),
    });
  },

  bulkInitiateRTO: (bulkInitiation) => {
    return fetchApi('/orders/rto/bulk-initiate', {
      method: 'POST',
      body: JSON.stringify(bulkInitiation),
    });
  },

  bulkRTOStatusUpdate: (bulkUpdate) => {
    return fetchApi('/orders/rto/bulk-status-update', {
      method: 'POST',
      body: JSON.stringify(bulkUpdate),
    });
  },

  bulkMarkRTODelivered: (bulkDelivery) => {
    return fetchApi('/orders/rto/bulk-mark-delivered', {
      method: 'POST',
      body: JSON.stringify(bulkDelivery),
    });
  },

  getRTOStatistics: (params = {}) => {
    const { employeeId, startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/rto/stats${queryString ? '?' + queryString : ''}`);
  },

  getRTOStatusSummary: (params = {}) => {
    const { employeeId, courier } = params;
    const queryParams = new URLSearchParams();
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (courier) queryParams.append('courier', courier);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/rto/status-summary${queryString ? '?' + queryString : ''}`);
  },

  getCourierRTOPerformance: () => fetchApi('/orders/rto/courier-performance'),

  getRTOReasonsAnalysis: (params = {}) => {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/rto/reasons-analysis${queryString ? '?' + queryString : ''}`);
  },

  processRTORefund: (orderId, refundDetails) => {
    return fetchApi(`/orders/rto/${orderId}/process-refund`, {
      method: 'POST',
      body: JSON.stringify(refundDetails),
    });
  },

  getRTOFinancialImpact: (params = {}) => {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/orders/rto/financial-impact${queryString ? '?' + queryString : ''}`);
  },

  addRTONote: (orderId, note) => {
    return fetchApi(`/orders/rto/${orderId}/add-note`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },
  getWorkflowOrders: async (status, employeeId, dateParams) => {
    const params = new URLSearchParams();

    if (employeeId) params.append('employeeId', employeeId);
    if (dateParams?.startDate) params.append('startDate', dateParams.startDate);
    if (dateParams?.endDate) params.append('endDate', dateParams.endDate);

    const query = params.toString() ? `?${params.toString()}` : '';

    // ✅ status null/undefined ho toh 'ALL' bhi mat lagao
    const statusPath = status ? `/${status}` : '';

    return fetchApi(`/workflow${statusPath}${query}`);
  },
  getAdminFilteredOrders: async (filters, dateParams) => {
    const params = new URLSearchParams();

    if (filters.status && filters.status !== 'All Status') {
      if (filters.status === 'PENDING' || filters.status === 'REJECTED') {
        params.append('approvalStatus', filters.status === 'PENDING' ? 'APPROVAL_PENDING' : 'REJECTED');
      } else {
        params.append('status', filters.status);
      }
    }

    if (filters.leadSource && filters.leadSource !== 'All Sources') params.append('source', filters.leadSource);
    if (filters.paymentMode && filters.paymentMode !== 'All Modes') params.append('paymentMode', filters.paymentMode);
    if (filters.campaign && filters.campaign !== 'All Campaigns') params.append('campaignName', filters.campaign);
    if (filters.state && filters.state !== 'All States') params.append('state', filters.state);
    if (filters.courier && filters.courier !== 'All Couriers') params.append('courier', filters.courier);
    if (filters.category && filters.category !== 'All Categories') params.append('category', filters.category);
    if (filters.orders && filters.orders !== 'All Orders') params.append('orderType', filters.orders);
    if (filters.product && filters.product !== 'All Products') params.append('productName', filters.product);

    // ✅ ID pass karo — naam nahi
    if (filters.orderById) params.append('orderBy', filters.orderById);
    if (filters.actionById) params.append('actionBy', filters.actionById);
    if (filters.teamLeadId) params.append('teamId', filters.teamLeadId);

    if (dateParams?.startDate) params.append('startDate', dateParams.startDate);
    if (dateParams?.endDate) params.append('endDate', dateParams.endDate);

    params.append('page', '0');
    params.append('size', '1000');

    return fetchApi(`/orders?${params.toString()}`);
  },
  getAllOrders: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders?${queryString}` : '/orders';
    return fetchApi(endpoint);
  },

  getOrderDetails: (id) => fetchApi(`/orders/${id}/details`),

  // UpdateOrder.js ise call karta tha lekin ordersApi me ye define hi nahi thi
  // (TypeError → "Failed to load order data"). getOrderDetails ka alias hai.
  getOrderById: (id) => fetchApi(`/orders/${id}/details`),

  createOrder: (orderData) => {
    return fetchApi('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  OrderStatus: (orderData, status) => {
    return fetchApi(`/orders/${orderData}/status?status=${status}`, {
      method: 'PATCH',
      body: JSON.stringify(orderData),
    });
  },

  getOrdersByApprovalStatus: (approvalStatus, userId = null) => {
    if (userId) {
      return fetchApi(`/orders?status=${approvalStatus}&userId=${userId}`);
    }
    return fetchApi(`/orders/order-status/${approvalStatus}`);
  },

  getOrdersByDateRange: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders/date-range?${queryString}` : '/orders/date-range';
    return fetchApi(endpoint);
  },

  searchOrders: (searchTerm) => {
    const queryString = new URLSearchParams({ searchTerm }).toString();
    return fetchApi(`/orders/search?${queryString}`);
  },

  updateOrderStatus: (id, status) => {
    const queryString = new URLSearchParams({ status }).toString();
    return fetchApi(`/orders/${id}/status?${queryString}`, {
      method: 'PATCH',
    });
  },

  updateApprovalStatus: (id, approvalStatus) => {
    const queryString = new URLSearchParams({ approvalStatus }).toString();
    return fetchApi(`/orders/${id}/approval-status?${queryString}`, {
      method: 'PATCH',
    });
  },

  updateShippingDetails: (id, shippingData) => {
    return fetchApi(`/orders/${id}/shipping`, {
      method: 'PATCH',
      body: JSON.stringify(shippingData),
    });
  },

  getOrderSummary: (employeeId = null) => {
    const params = employeeId ? { employeeId } : {};
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/dashboard/orders/summary?${queryString}` : '/dashboard/orders/summary';
    return fetchApi(endpoint);
  },

  getOrdersGroupedByApproval: (userId = null) => {
    const params = userId ? { userId } : {};
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders/grouped-by-approval?${queryString}` : '/orders/grouped-by-approval';
    return fetchApi(endpoint);
  },

  resendWhatsApp: (id) => {
    return fetchApi(`/orders/${id}/resend-whatsapp`, {
      method: 'POST',
    });
  },

  bulkUpdateStatus: (orderIds, status) => {
    return fetchApi('/orders/bulk/status', {
      method: 'PATCH',
      body: JSON.stringify({ orderIds, status }),
    });
  },

  bulkUpdateApprovalStatus: (orderIds, approvalStatus) => {
    return fetchApi('/orders/bulk/approval-status', {
      method: 'PATCH',
      body: JSON.stringify({ orderIds, approvalStatus }),
    });
  },

  getDraftOrders: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/orders/drafts?${queryString}` : '/orders/drafts';
    return fetchApi(endpoint);
  },

  getDraftOrderById: (id) => fetchApi(`/orders/draft/${id}`),

  createDraftOrder: (draftData) => {
    return fetchApi('/orders/draft', {
      method: 'POST',
      body: JSON.stringify(draftData),
    });
  },

  updateDraftOrder: (id, draftData) => {
    return fetchApi(`/orders/draft/${id}`, {
      method: 'PUT',
      body: JSON.stringify(draftData),
    });
  },

  deleteDraftOrder: (id) => {
    return fetchApi(`/orders/draft/${id}`, {
      method: 'DELETE',
    });
  },

  convertDraftToOrder: (id) => {
    return fetchApi(`/orders/draft/${id}/place`, {
      method: 'POST',
    });
  },
};

// Products API restored for Add New Order dependency
export const productsApi = {
  // --- Products ---
  getAllProducts: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/products${q ? '?' + q : ''}`);
  },
  getProductById: (id) => fetchApi(`/products/${id}`),
  searchProducts: (query) => fetchApi(`/products/search?query=${encodeURIComponent(query)}`),
  getProductsByCategory: (category) => fetchApi(`/products/category/${encodeURIComponent(category)}`),
  getProductsByCategoryAndSubCategory: async (category, subCategory) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
    try {
      const res = await window.fetch(
        `${BASE_URL}/products/category/${encodeURIComponent(category)}/subcategory/${encodeURIComponent(subCategory)}`,
        { headers, mode: 'cors' }
      );
      if (res.ok) return res.json();
      console.warn(`getProductsByCategoryAndSubCategory: ${res.status} — returning []`);
      return [];
    } catch (e) {
      console.warn('getProductsByCategoryAndSubCategory error:', e.message);
      return [];
    }
  },
  getProductsByType: (type) => fetchApi(`/products/type/${type}`),

  // Create/Update Product (Multipart - with image)
  createProduct: async (productData, imageFile) => {
    const formData = new FormData();
    if (imageFile) formData.append('productImage', imageFile);
    formData.append('productData', new Blob([JSON.stringify(productData)], { type: 'application/json' }));

    const makeRequest = (token) => handleDirectFetch(`${BASE_URL}/products`, {
      method: 'POST',
      body: formData,
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
    });

    const response = await makeRequest(localStorage.getItem('authToken'));
    return await handleResponse(response, makeRequest);
  },

  updateProduct: async (id, productData, imageFile) => {
    const formData = new FormData();
    if (imageFile) formData.append('productImage', imageFile);
    formData.append('productData', new Blob([JSON.stringify(productData)], { type: 'application/json' }));

    const makeRequest = (token) => handleDirectFetch(`${BASE_URL}/products/${id}`, {
      method: 'PUT',
      body: formData,
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
    });

    const response = await makeRequest(localStorage.getItem('authToken'));
    return await handleResponse(response, makeRequest);
  },

  // Create/Update Product (JSON only)
  createProductJson: (data) => fetchApi('/products/json', { method: 'POST', body: JSON.stringify(data) }),
  updateProductJson: (id, data) => fetchApi(`/products/${id}/json`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteProduct: (id) => fetchApi(`/products/${id}`, { method: 'DELETE' }),
  // existing functions ke saath add karo
  toggleProductStatus: (id) => fetchApi(`/products/${id}/toggle-status`, { method: 'PATCH' }),

  // Image Upload Helpers
  uploadIngredientImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/products/upload/ingredient-image`, {
      method: 'POST',
      body: formData,
    });
    return await handleResponse(response);
  },

  uploadKitImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/products/upload/kit-image`, {
      method: 'POST',
      body: formData,
    });
    return await handleResponse(response);
  },

  // Dropdown helpers
  getAllCategories: () => fetchApi('/products/categories'),
  // ⚠️ getSubCategories neeche define hai (product-subcategories endpoint).
  //    Yahan ek duplicate key thi jo JS silently drop kar deta tha.

  // --- Product Categories ---
  getAllProductCategories: (search = '') => {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return fetchApi(`/product-categories${q}`);
  },
  getActiveProductCategories: () => fetchApi('/product-categories/active'),
  getProductCategoryById: (id) => fetchApi(`/product-categories/${id}`),
  createProductCategory: (data) => fetchApi('/product-categories', { method: 'POST', body: JSON.stringify(data) }),
  updateProductCategory: (id, data) => fetchApi(`/product-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleProductCategoryStatus: (id) => fetchApi(`/product-categories/${id}/toggle`, { method: 'PATCH' }),
  deleteProductCategory: (id) => fetchApi(`/product-categories/${id}`, { method: 'DELETE' }),

  // --- Product Sub Categories ---
  // ⚠️ IMPORTANT: This uses a safe direct fetch to avoid the global
  // handleResponse → performLogout chain when 401/500 is returned.
  getAllProductSubcategories: () => fetchApi('/product-subcategories'),
  createProductSubcategory: (data) =>
    fetchApi('/product-subcategories', {
      method: 'POST',
      body: JSON.stringify({
        subCategoryName: data.subCategoryName,
        categoryName: data.categoryName,
        createdBy: 'Admin'
      })
    }),
  updateProductSubcategory: (id, data) =>
    fetchApi(`/product-subcategories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  toggleProductSubcategoryStatus: (id) => fetchApi(`/product-subcategories/${id}/toggle`, { method: 'PATCH' }),
  deleteProductSubcategory: (id) => fetchApi(`/product-subcategories/${id}`, { method: 'DELETE' }),
  getSubCategories: async (category) => {
    if (!category) return [];

    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    try {
      // ✅ Sahi endpoint — /products/categories/{category}/subcategories
      const res = await window.fetch(
        `${BASE_URL}/product-subcategories?category=${category}`,
        { headers, mode: 'cors' }
      );
      console.log("res", res)
      if (res.ok) return res.json();

      console.warn(`getSubCategories: ${res.status} for "${category}"`);
      return [];
    } catch (e) {
      console.warn('getSubCategories error:', e.message);
      return [];
    }
  },
  // --- Product Kits ---
  getAllKits: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/product-kits${q ? '?' + q : ''}`);
  },
  searchKits: (query) => fetchApi(`/product-kits?search=${encodeURIComponent(query)}`),
  getKitsByCategory: (category) => fetchApi(`/product-kits?category=${encodeURIComponent(category)}`),
  getActiveKits: () => fetchApi('/product-kits/active'),
  getKitById: (id) => fetchApi(`/product-kits/${id}`),
  createKit: (data) => fetchApi('/product-kits', { method: 'POST', body: JSON.stringify(data) }),
  updateKit: (id, data) => fetchApi(`/product-kits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteKit: (id) => fetchApi(`/product-kits/${id}`, { method: 'DELETE' }),
  toggleKitStatus: (id) => fetchApi(`/product-kits/${id}/toggle`, { method: 'PATCH' }),
  activateKit: (id) => fetchApi(`/product-kits/${id}/activate`, { method: 'PATCH' }),
  deactivateKit: (id) => fetchApi(`/product-kits/${id}/deactivate`, { method: 'PATCH' }),

  addKitItem: (kitId, data) => fetchApi(`/product-kits/${kitId}/items`, { method: 'POST', body: JSON.stringify(data) }),
  removeKitItem: (kitId, itemId) => fetchApi(`/product-kits/${kitId}/items/${itemId}`, { method: 'DELETE' }),
  updateKitItemQuantity: (kitId, itemId, quantity) =>
    fetchApi(`/product-kits/${kitId}/items/${itemId}/quantity`, { method: 'PATCH', body: JSON.stringify({ quantity }) }),

  // --- Bulk & System ---
  bulkUploadExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/products/bulk-upload`, {
      method: 'POST',
      body: formData,
    });
    return await handleResponse(response);
  },
  downloadTemplate: async () => {
    const response = await handleDirectFetch(`${BASE_URL}/products/bulk-upload/template`, {
      method: 'GET',
    });
    if (!response.ok) throw new Error('Failed to download template');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_bulk_upload_template.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  getHealth: () => fetchApi('/products/health'),
  // ⚠️ ASSUMED ENDPOINT — Products.js ise call karta tha lekin ye define hi nahi thi.
  //    Call-site `.catch(() => null)` karta hai, so 404 safe hai. Backend se verify karein.
  getKitHealth: () => fetchApi('/product-kits/health'),
};

// Leads API
export const leadsApi = {
  getAllLeads: (query = '') =>
    fetchApi(`/leads${query}`),

  getCountsByStatus: (teamId = null, query = '') => {
    const params = new URLSearchParams(query.replace('?', ''));
    if (teamId) params.set('teamId', teamId);
    const queryString = params.toString();
    return fetchApi(`/leads/counts/by-status${queryString ? '?' + queryString : ''}`);
  },

  exportLeadsExcel: async (query = '') => {
    const response = await handleDirectFetch(
      `${BASE_URL}/leads/export/excel${query}`,
      { method: 'GET' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Export failed: ${response.status}`);
    }
    return response.blob();
  },

  getLeadsByTeam: (teamName) => fetchApi(`/leads?teamName=${encodeURIComponent(teamName)}`),

  getLeadsByEmployee: async (employeeId, extraParams = {}) => {
    try {
      const params = new URLSearchParams();
      // extraParams mein jo bhi aaye add karo
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await handleDirectFetch(
        `${BASE_URL}/leads/employee/${employeeId}${queryString}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching employee leads:', error);
      throw error;
    }
  },

  // ✅ UPDATED
  bulkUploadLeads: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await handleDirectFetch(`${BASE_URL}/leads/bulk-upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Error bulk uploading leads:', error);
      throw error;
    }
  },

  getLeadById: (id) => fetchApi(`/leads/${id}`),
  createLead: (leadData) => fetchApi('/leads', {
    method: 'POST',
    body: JSON.stringify(leadData),
  }),
  updateLead: (id, leadData) => fetchApi(`/leads/${id}`, {
    method: 'PUT',
    body: JSON.stringify(leadData),
  }),
  updateLeadDisposition: (leadId, dispositionName, dispositionId) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('crm:disposition_saved', {
          detail: { leadId, dispositionName, dispositionId },
        })
      );
    }
    return fetchApi(`/leads/${leadId}/disposition?dispositionName=${dispositionName}`, {
      method: 'PATCH',
    });
  },
  deleteLead: (id) => fetchApi(`/leads/${id}`, { method: 'DELETE' }),
  searchLeads: (query) => {
    const queryString = new URLSearchParams({ query }).toString();
    return fetchApi(`/leads/search?${queryString}`);
  },
  getLeadsByStatus: (status, employeeId) => fetchApi(`/leads/status/${status}/employee/${employeeId}`),
  getLeadsByUser: (userId) => fetchApi(`/leads/user/${userId}`),
  assignLead: (leadId, employeeId) => fetchApi(`/leads/${leadId}/assign/${employeeId}`, { method: 'PUT' }),
  assignLeadToUser: (leadId, userId, campaignName) => fetchApi(`/leads/${leadId}/assign/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ campaignName })
  }),
  bulkCreateLeads: (leadDTOs) => fetchApi('/leads/bulk-create', {
    method: 'POST',
    body: JSON.stringify(leadDTOs),
  }),
  getDashboardStats: (userId) => fetchApi(`/leads/dashboard/${userId}`),
  updateLeadStatus: (id, status) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('crm:disposition_saved', {
          detail: { leadId: id, dispositionName: status },
        })
      );
    }
    return fetchApi(`/leads/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
  getTopPerformersAPI: () => fetchApi('/dashboard/stats/top-performers?limit=4&metric=orderCount'),

  // ── Lead History APIs ──
  getLeadHistory: (leadId) => fetchApi(`/leads/${leadId}/history`),
  getLeadCallHistory: (leadId, page = 0, size = 20) => fetchApi(`/leads/${leadId}/history/calls?page=${page}&size=${size}`),
  getLeadAssignHistory: (leadId, page = 0, size = 20) => fetchApi(`/leads/${leadId}/history/assignments?page=${page}&size=${size}`),
  getLeadStatusHistory: (leadId, page = 0, size = 20) => fetchApi(`/leads/${leadId}/history/status?page=${page}&size=${size}`),
  logLeadCall: (leadId, callData) => fetchApi(`/leads/${leadId}/call-logs`, {
    method: 'POST',
    body: JSON.stringify(callData),
  }),
};

// Employees API
export const employeesApi = {
  getAllEmployees: () => fetchApi('/employees'),
  getEmployeeById: (employeeId) => fetchApi(`/employees/${employeeId}`),
  getEmployeesByTeam: (teamId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchApi(`/employees/team/${teamId}${query ? '?' + query : ''}`);
  },

  searchEmployees: (name) => fetchApi(`/employees/search?name=${encodeURIComponent(name)}`),
  createEmployee: (employeeData) => fetchApi('/employees', {
    method: 'POST',
    body: JSON.stringify(employeeData),
  }),
  updateEmployee: (id, employeeData) => fetchApi(`/employees/${id}`, {
    method: 'PUT',
    body: JSON.stringify(employeeData),
  }),
  changeEmployeeTeam: (id, teamData) => fetchApi(`/employees/${id}/change-team`, {
    method: 'PUT',
    body: JSON.stringify(teamData),
  }),
  updateEmployeeStatus: (id, isActive) => fetchApi(`/employees/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  }),
  deleteEmployee: (id) => fetchApi(`/employees/${id}`, { method: 'DELETE' }),

  // ✅ UPDATED
  bulkUploadEmployees: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await handleDirectFetch(`${BASE_URL}/employees/bulk-upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }
    return response.json();
  },
};

// Floors API
export const floorsApi = {
  getAllFloors: async () => {
    try {
      const response = await handleDirectFetch(`${BASE_URL}/floors`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching floors:', error);
      return [];
    }
  },

  getFloorById: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/floors/${id}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  createFloor: async (floorData) => {
    const response = await handleDirectFetch(`${BASE_URL}/floors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(floorData),
    });
    return handleResponse(response);
  },

  updateFloor: async (id, floorData) => {
    const response = await handleDirectFetch(`${BASE_URL}/floors/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(floorData),
    });
    return handleResponse(response);
  },

  deleteFloor: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/floors/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.status === 204) return { success: true };
    return handleResponse(response);
  },

  assignTeamsToFloor: async (floorId, teamIds) => {
    const response = await handleDirectFetch(`${BASE_URL}/floors/${floorId}/assign-teams`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(teamIds),
    });
    return handleResponse(response);
  },

  getTeamsByFloor: async (floorId) => {
    try {
      const response = await handleDirectFetch(`${BASE_URL}/teams/floor/${floorId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching teams by floor:', error);
      return [];
    }
  },
};

// Verified Orders API
export const verifiedOrdersApi = {
  getVerifiedOrders: async (userId = null) => {
    try {
      const url = userId
        ? `${BASE_URL}/orders/approval-status/CONFIRMED/user/${userId}`
        : `${BASE_URL}/orders/approval-status/CONFIRMED`;
      const response = await handleDirectFetch(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching verified orders:', error);
      return [];
    }
  },

  getOrdersByApprovalStatus: async (approvalStatus, userId = null) => {
    try {
      const url = userId
        ? `${BASE_URL}/orders/approval-status/${approvalStatus}/user/${userId}`
        : `${BASE_URL}/orders/approval-status/${approvalStatus}`;
      const response = await handleDirectFetch(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching orders by approval status:', error);
      return [];
    }
  },

  getOrdersByStatusAndApproval: async (status, approvalStatus) => {
    try {
      const response = await handleDirectFetch(
        `${BASE_URL}/orders/status-approval?status=${status}&approvalStatus=${approvalStatus}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching orders by status and approval:', error);
      return [];
    }
  },

  countVerifiedOrders: async (approvalStatus) => {
    try {
      const response = await handleDirectFetch(
        `${BASE_URL}/orders/count/approval-status/${approvalStatus}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const text = await response.text();
      return parseInt(text);
    } catch (error) {
      console.error('Error counting verified orders:', error);
      return 0;
    }
  },

  verifyOrder: async (orderId) => {
    const response = await handleDirectFetch(`${BASE_URL}/orders/${orderId}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  markOrderAsPending: async (orderId) => {
    const response = await handleDirectFetch(`${BASE_URL}/orders/${orderId}/mark-pending`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  updateRemarks: async (orderId, remarks) => {
    const response = await handleDirectFetch(`${BASE_URL}/orders/${orderId}/remarks`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(remarks),
    });
    return handleResponse(response);
  },

  exportToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await handleDirectFetch(`${BASE_URL}/orders/export/excel?${params}`, {});
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw error;
    }
  },
};

// Departments API
export const departmentsApi = {
  getAllDepartments: () => fetchApi('/departments'),
  createDepartment: (departmentData) => fetchApi('/departments', {
    method: 'POST',
    body: JSON.stringify(departmentData),
  }),
  updateDepartment: (id, departmentData) => fetchApi(`/departments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(departmentData),
  }),
};

// Teams API
export const teamsApi = {
  getAllTeams: () => fetchApi('/teams'),
  createTeam: (teamData) => fetchApi('/teams', {
    method: 'POST',
    body: JSON.stringify(teamData),
  }),
  updateTeam: (id, teamData) => fetchApi(`/teams/${id}`, {
    method: 'PUT',
    body: JSON.stringify(teamData),
  }),
  updateTeamStatus: (id, isActive) => fetchApi(`/teams/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive, status: isActive ? 'ACTIVE' : 'INACTIVE', active: isActive }),
  }),
  toggleTeamStatus: (id) => fetchApi(`/teams/${id}/toggle`, {
    method: 'PATCH',
  }),
};

// Designations API
export const designationsApi = {
  getAllDesignations: async () => {
    try {
      const response = await handleDirectFetch(`${BASE_URL}/designations`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch designations`);
      const data = await response.json();
      return data?.data || data?.designations || data || [];
    } catch (error) {
      console.error('Error fetching designations:', error);
      if (error.message.includes('403')) {
        console.warn('⚠️ Access denied to designations API, using fallback list');
        return [
          'Administrator', 'Manager', 'Team Lead', 'Senior Developer',
          'Junior Developer', 'HR Executive', 'Sales Executive', 'Accountant', 'Marketing Executive'
        ];
      }
      return [];
    }
  },

  updateDesignation: (id, data) => fetchApi(`/designations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  createDesignation: async (designationData) => {
    const response = await handleDirectFetch(`${BASE_URL}/designations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(designationData),
    });
    if (!response.ok) throw new Error('Failed to create designation');
    return response.json();
  },

  // UserPermission.js ise call karta tha lekin ye define hi nahi thi.
  deleteDesignation: (id) => fetchApi(`/designations/${id}`, { method: 'DELETE' }),
};

// Partners API
export const partnersApi = {
  getAllPartners: async () => {
    const response = await handleDirectFetch(`${BASE_URL}/partners`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  getPartnerById: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/partners/${id}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  createPartner: async (partnerData) => {
    const response = await handleDirectFetch(`${BASE_URL}/partners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partnerData),
    });
    return handleResponse(response);
  },

  updatePartner: async (id, partnerData) => {
    const response = await handleDirectFetch(`${BASE_URL}/partners/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partnerData),
    });
    return handleResponse(response);
  },

  deletePartner: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/partners/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.status === 204) return { success: true };
    return handleResponse(response);
  },

  addShippingAddress: async (partnerId, addressData) => {
    const backendAddressData = {
      addressLine: addressData.address,
      landmark: addressData.landmark || '',
      pincode: addressData.pincode,
      city: addressData.city || '',
      district: addressData.district || '',
      state: addressData.state || '',
      country: addressData.country || 'India',
      isDefault: addressData.isDefault || false
    };
    const response = await handleDirectFetch(`${BASE_URL}/partners/${partnerId}/shipping-addresses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(backendAddressData),
    });
    return handleResponse(response);
  },
};

// Purchase Orders API
export const purchaseOrdersApi = {
  getAllOrders: async () => {
    const response = await handleDirectFetch(`${BASE_URL}/purchases`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  getOrderById: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/purchases/${id}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  createOrder: async (orderData) => {
    const response = await handleDirectFetch(`${BASE_URL}/purchases`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData),
    });
    return handleResponse(response);
  },

  updateOrder: async (id, orderData) => {
    const response = await handleDirectFetch(`${BASE_URL}/purchases/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData),
    });
    return handleResponse(response);
  },

  deleteOrder: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/purchases/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.status === 204) return { success: true };
    return handleResponse(response);
  },
};

// Dashboard API
export const dashboardApi = {
  getRealtimeAttendance: () => fetchApi('/dashboard/realtime/attendance'),
  // ── Floor Analytics ──────────────────────────────────────────
  getFloorAnalyticsOverview: (params = {}) => {
    const q = new URLSearchParams();
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApi(`/dashboard/analytics/floor-overview?${q.toString()}`);
  },

  getFloorEmployeePerformance: (params = {}) => {
    const q = new URLSearchParams();
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 10);
    return fetchApi(`/dashboard/analytics/floor-employee-performance?${q.toString()}`);
  },
  getFloorGrandTotal: (params = {}) => {
    const q = new URLSearchParams();
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApi(`/dashboard/analytics/floor-grand-total?${q.toString()}`);
  },
  getRealtimeEmployees: (params = {}) => {
    const { search, page = 0, size = 10 } = params;
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page);
    queryParams.append('size', size);
    return fetchApi(`/dashboard/realtime/employees?${queryParams.toString()}`);
  },

  getAHTDispositions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.leadType) q.append('leadType', params.leadType);
    if (params.teamId) q.append('teamId', params.teamId);
    const qs = q.toString();
    return fetchApi(`/dashboard/campaign/aht-dispositions${qs ? '?' + qs : ''}`);
  },

  getAHTDispositionsDrilldown: (params = {}) => {
    const q = new URLSearchParams();
    if (params.dispositionType) q.append('dispositionType', params.dispositionType);
    if (params.timeBucket) q.append('timeBucket', params.timeBucket);
    if (params.leadType) q.append('leadType', params.leadType);
    if (params.search) q.append('search', params.search);
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    const qs = q.toString();
    return fetchApi(`/dashboard/campaign/aht-dispositions/drilldown${qs ? '?' + qs : ''}`);
  },

  getCallRecording: (leadId) =>
    fetchApi(`/dashboard/campaign/aht-dispositions/recording/${leadId}`),
  getLeadStatusBreakdown: (query = '') =>
    fetchApi(`/dashboard/leads/status-breakdown${query}`),

  getLeadConversionSummary: (query = '') =>
    fetchApi(`/dashboard/leads/conversion-summary${query}`),

  getLeadsSummary: (query = '') =>
    fetchApi(`/dashboard/leads/summary${query}`),

  getLeadsStatusBreakdown: (query = '') =>
    fetchApi(`/dashboard/leads/status-breakdown${query}`),

  getLeadsConversionSummary: (query = '') =>
    fetchApi(`/dashboard/leads/conversion-summary${query}`),

  getLeadsBySource: (query = '') =>
    fetchApi(`/dashboard/leads/by-source${query}`),



  getOrderSummary: (params = '') => {
    return fetchApi(`/dashboard/orders/summary${params}`);
  },

  getOrderStatusBreakdown: (employeeId = null) => {
    const queryString = employeeId ? `?employeeId=${employeeId}` : '';
    return fetchApi(`/dashboard/orders/status-breakdown${queryString}`);
  },

  getOrdersByState: (params = {}) => {
    const { status, employeeId } = params;
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    if (employeeId) queryParams.append('employeeId', employeeId);
    const queryString = queryParams.toString();
    return fetchApi(`/dashboard/orders/by-state${queryString ? '?' + queryString : ''}`);
  },

  getOrderStateCards: (status = null) => {
    const queryString = status ? `?status=${status}` : '';
    return fetchApi(`/dashboard/orders/state-cards${queryString}`);
  },

  // ── NAYA (floorId add kiya) ──
  getAnalyticsOverview: (params = {}) => {
    const { startDate, endDate, teamId, employeeId, floorId } = params;
    const q = new URLSearchParams();
    if (floorId) q.append('floorId', floorId);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    if (teamId) q.append('teamId', teamId);
    if (employeeId) q.append('employeeId', employeeId);
    const qs = q.toString();
    return fetchApi(`/dashboard/analytics/overview${qs ? '?' + qs : ''}`);
  },

  // ── NAYA (floorId add kiya) ──
  getEmployeePerformance: (params = {}) => {
    const { startDate, endDate, teamId, employeeId, floorId, page = 0, size = 10 } = params;
    const q = new URLSearchParams();
    if (floorId) q.append('floorId', floorId);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    if (teamId) q.append('teamId', teamId);
    if (employeeId) q.append('employeeId', employeeId);
    q.append('page', page);
    q.append('size', size);
    return fetchApi(`/dashboard/analytics/employee-performance?${q.toString()}`);
  },

  // ── NAYA (floorId add kiya) ──
  getGrandTotal: (params = {}) => {
    const { startDate, endDate, teamId, employeeId, floorId } = params;
    const q = new URLSearchParams();
    if (floorId) q.append('floorId', floorId);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    if (teamId) q.append('teamId', teamId);
    if (employeeId) q.append('employeeId', employeeId);
    const qs = q.toString();
    return fetchApi(`/dashboard/analytics/grand-total${qs ? '?' + qs : ''}`);
  },

  getAHTBuckets: (params = {}) => {
    const { campaignType, teamId } = params;
    const queryParams = new URLSearchParams();
    if (campaignType) queryParams.append('campaignType', campaignType);
    if (teamId) queryParams.append('teamId', teamId);
    const queryString = queryParams.toString();
    return fetchApi(`/dashboard/campaign/aht-buckets${queryString ? '?' + queryString : ''}`);
  },

  getAHTCampaignWise: (leadType = null) => {
    const queryString = leadType ? `?leadType=${leadType}` : '';
    return fetchApi(`/dashboard/campaign/aht-campaign-wise${queryString}`);
  },

  getAHTCampaignSummary: (leadType = null) => {
    const queryString = leadType ? `?leadType=${leadType}` : '';
    return fetchApi(`/dashboard/campaign/aht-summary${queryString}`);
  },

  getDateRangeStats: (params = {}) => {
    const { startDate, endDate, employeeId, metric } = params;
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (metric) queryParams.append('metric', metric);
    return fetchApi(`/dashboard/stats/date-range?${queryParams.toString()}`);
  },

  getTeamComparison: (params = {}) => {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/dashboard/stats/team-comparison${queryString ? '?' + queryString : ''}`);
  },

  getTopPerformers: (params = {}) => {
    const { limit = 5, metric, startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit);
    if (metric) queryParams.append('metric', metric);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    return fetchApi(`/dashboard/stats/top-performers?${queryParams.toString()}`);
  },

  exportDashboardData: async (params = {}) => {
    const { dashboardType, startDate, endDate, status, states } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('dashboardType', dashboardType);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (status) queryParams.append('status', status);
    if (states && states.length > 0) queryParams.append('states', states.join(','));

    try {
      const response = await handleDirectFetch(
        `${BASE_URL}/dashboard/export?${queryParams.toString()}`, {}
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `dashboard_${dashboardType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      return { success: true };
    } catch (error) {
      console.error('Error exporting dashboard:', error);
      throw error;
    }
  },

  getMyDashboard: (employeeId = null) => {
    const queryString = employeeId ? `?employeeId=${employeeId}` : '';
    return fetchApi(`/dashboard/my-dashboard${queryString}`);
  },

  getQuickStats: (employeeId = null) => {
    const queryString = employeeId ? `?employeeId=${employeeId}` : '';
    return fetchApi(`/dashboard/quick-stats${queryString}`);
  },
};

// Workflow API
export const workflowApi = {
  getVerifiedOrders: (params = {}) => {
    const { employeeId, searchTerm } = params;
    const queryParams = new URLSearchParams();
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (searchTerm) queryParams.append('searchTerm', searchTerm);
    const queryString = queryParams.toString();
    return fetchApi(`/workflow/verified${queryString ? '?' + queryString : ''}`);
  },

  verifyOrder: (orderId) => fetchApi(`/workflow/verified/${orderId}/verify`, { method: 'POST' }),

  holdOrder: (orderId, reason) => fetchApi(`/workflow/verified/${orderId}/hold`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  markAsPending: (orderId, reason) => fetchApi(`/workflow/verified/${orderId}/pending`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  getDispatchReadyOrders: (params = {}) => {
    const { courier, employeeId } = params;
    const queryParams = new URLSearchParams();
    if (courier) queryParams.append('courier', courier);
    if (employeeId) queryParams.append('employeeId', employeeId);
    const queryString = queryParams.toString();
    return fetchApi(`/workflow/dispatch${queryString ? '?' + queryString : ''}`);
  },

  dispatchOrder: (orderId, dispatchRequest) => fetchApi(`/workflow/dispatch/${orderId}`, {
    method: 'POST',
    body: JSON.stringify(dispatchRequest),
  }),

  bulkDispatch: (bulkRequest) => fetchApi('/workflow/dispatch/bulk', {
    method: 'POST',
    body: JSON.stringify(bulkRequest),
  }),

  updateCourierDetails: (orderId, courierUpdate) => fetchApi(`/workflow/dispatch/${orderId}/courier`, {
    method: 'PUT',
    body: JSON.stringify(courierUpdate),
  }),

  getInTransitOrders: (params = {}) => {
    const { courier, status, employeeId } = params;
    const queryParams = new URLSearchParams();
    if (courier) queryParams.append('courier', courier);
    if (status) queryParams.append('status', status);
    if (employeeId) queryParams.append('employeeId', employeeId);
    const queryString = queryParams.toString();
    return fetchApi(`/workflow/in-transit${queryString ? '?' + queryString : ''}`);
  },

  updateTransitStatus: (orderId, statusUpdate) => fetchApi(`/workflow/in-transit/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify(statusUpdate),
  }),

  markAsOutForDelivery: (orderId) => fetchApi(`/workflow/in-transit/${orderId}/mark-ofd`, { method: 'POST' }),

  getOFDOrders: (params = {}) => {
    const { employeeId, courier } = params;
    const queryParams = new URLSearchParams();
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (courier) queryParams.append('courier', courier);
    const queryString = queryParams.toString();
    return fetchApi(`/workflow/ofd${queryString ? '?' + queryString : ''}`);
  },

  assignOFDOrders: (assignment) => fetchApi('/workflow/ofd/assign', {
    method: 'POST',
    body: JSON.stringify(assignment),
  }),

  updateOFDCustomerStatus: (orderId, customerStatus) => fetchApi(`/workflow/ofd/${orderId}/customer-status`, {
    method: 'PUT',
    body: JSON.stringify(customerStatus),
  }),

  markOFDAsDelivered: (orderId, confirmation = null) => fetchApi(`/workflow/ofd/${orderId}/deliver`, {
    method: 'POST',
    body: confirmation ? JSON.stringify(confirmation) : null,
  }),

  getDeliveredOrders: (params = {}) => {
    const { courier, employeeId, startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (courier) queryParams.append('courier', courier);
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/workflow/delivered${queryString ? '?' + queryString : ''}`);
  },

  getDeliveryStatistics: (employeeId = null) => {
    const queryString = employeeId ? `?employeeId=${employeeId}` : '';
    return fetchApi(`/workflow/delivered/stats${queryString}`);
  },

  bulkStatusUpdate: (bulkUpdate) => fetchApi('/workflow/bulk/status', {
    method: 'POST',
    body: JSON.stringify(bulkUpdate),
  }),

  bulkAssign: (bulkAssignment) => fetchApi('/workflow/bulk/assign', {
    method: 'POST',
    body: JSON.stringify(bulkAssignment),
  }),

  getOrderTracking: (orderId) => fetchApi(`/workflow/tracking/${orderId}`),

  updateTracking: (orderId, trackingUpdate) => fetchApi(`/workflow/tracking/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify(trackingUpdate),
  }),

  logCustomerInteraction: (orderId, interaction) => fetchApi(`/workflow/${orderId}/interaction`, {
    method: 'POST',
    body: JSON.stringify(interaction),
  }),

  resendNotification: (orderId, channel) => fetchApi(`/workflow/${orderId}/resend-notification?channel=${encodeURIComponent(channel)}`, {
    method: 'POST',
  }),

  getWorkflowMetrics: (params = {}) => {
    const { employeeId, startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (employeeId) queryParams.append('employeeId', employeeId);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/workflow/metrics${queryString ? '?' + queryString : ''}`);
  },

  getCourierPerformance: () => fetchApi('/workflow/courier-performance'),

  getStageSummary: (employeeId = null) => {
    const queryString = employeeId ? `?employeeId=${employeeId}` : '';
    return fetchApi(`/workflow/stage-summary${queryString}`);
  },
};

// Order Campaign API
export const orderCampaignApi = {
  getAllCampaigns: () => fetchApi('/order-campaign'),
  getCampaignById: (id) => fetchApi(`/order-campaign/${id}`),
  getCampaignByName: (campaignName) => fetchApi(`/order-campaign/name/${encodeURIComponent(campaignName)}`),
  getCampaignsByStatus: (status) => fetchApi(`/order-campaign/status/${status}`),
  createCampaign: (data) => fetchApi('/order-campaign', { method: 'POST', body: JSON.stringify(data) }),
  updateCampaign: (id, data) => fetchApi(`/order-campaign/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleCampaignStatus: (id) => fetchApi(`/order-campaign/${id}/toggle-status`, { method: 'PATCH' }),
  deleteCampaign: (id) => fetchApi(`/order-campaign/${id}`, { method: 'DELETE' }),
};

// Lead Disposition API
export const leadDispositionApi = {
  getAllDispositions: () => fetchApi('/lead-disposition'),
  getDispositionById: (id) => fetchApi(`/lead-disposition/${id}`),
  getDispositionByName: (dispositionName) => fetchApi(`/lead-disposition/name/${encodeURIComponent(dispositionName)}`),
  getDispositionsByStatus: (status) => fetchApi(`/lead-disposition/status/${status}`),
  createDisposition: (data) => fetchApi('/lead-disposition', { method: 'POST', body: JSON.stringify(data) }),
  updateDisposition: (id, data) => fetchApi(`/lead-disposition/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleDispositionStatus: (id) => fetchApi(`/lead-disposition/${id}/toggle-status`, { method: 'PATCH' }),
  deleteDisposition: (id) => fetchApi(`/lead-disposition/${id}`, { method: 'DELETE' }),
};

// Attendance API
export const attendanceApi = {
  // ── Employee APIs ──
  punchIn: (employeeId) => fetchApi(`/attendance/punch-in/${employeeId}`, { method: 'POST' }),
  punchOut: (employeeId) => fetchApi(`/attendance/punch-out/${employeeId}`, { method: 'POST' }),
  startLunchBreak: (employeeId) => fetchApi(`/attendance/start-lunch-break/${employeeId}`, { method: 'POST' }),
  endLunchBreak: (employeeId) => fetchApi(`/attendance/end-lunch-break/${employeeId}`, { method: 'POST' }),
  startTeaBreak: (employeeId) => fetchApi(`/attendance/start-tea-break/${employeeId}`, { method: 'POST' }),
  endTeaBreak: (employeeId) => fetchApi(`/attendance/end-tea-break/${employeeId}`, { method: 'POST' }),

  getTodayAttendance: (employeeId) => fetchApi(`/attendance/today/${employeeId}`),
  getAttendanceByDate: (date) => fetchApi(`/attendance/date/${date}`),

  getAttendanceHistory: (employeeId, startDate, endDate) => {
    const q = new URLSearchParams({ startDate, endDate }).toString();
    return fetchApi(`/attendance/history/${employeeId}?${q}`);
  },

  // ✅ NEW — employee monthly summary
  getMonthlySummary: (employeeId, year, month) => {
    const q = new URLSearchParams({ year, month }).toString();
    return fetchApi(`/attendance/my-monthly-summary/${employeeId}?${q}`);
  },

  // ── Admin APIs ──

  // ✅ NEW — filtered admin list (replaces getRealtimeEmployees for admin tab)
  getAdminList: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.status) q.append('status', params.status);
    if (params.currentStatus) q.append('currentStatus', params.currentStatus);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.search) q.append('search', params.search);
    if (params.isLate !== undefined) q.append('isLate', params.isLate);
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    return fetchApi(`/attendance/admin/list${q.toString() ? '?' + q.toString() : ''}`);
  },

  // ✅ NEW — admin monthly overview (all employees)
  getMonthlyOverview: (year, month) => {
    const q = new URLSearchParams({ year, month }).toString();
    return fetchApi(`/attendance/admin/monthly-overview?${q}`);
  },

  // ✅ NEW — admin monthly summary for one employee
  getAdminMonthlySummary: (employeeId, year, month) => {
    const q = new URLSearchParams({ year, month }).toString();
    return fetchApi(`/attendance/admin/monthly-summary/${employeeId}?${q}`);
  },

  // ✅ NEW — single employee on a single date
  getEmployeeDateDetail: (employeeId, date) =>
    fetchApi(`/attendance/admin/employee/${employeeId}/date/${date}`),
  // ✅ NEW — get all employee
  getAllEmployee: (employeeId, date) =>
    fetchApi(`/employees`),
  // ✅ NEW — admin update attendance record
  updateAttendance: (attendanceId, data) =>
    fetchApi(`/attendance/admin/update/${attendanceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ✅ NEW — admin delete attendance record
  deleteAttendance: (attendanceId) =>
    fetchApi(`/attendance/admin/delete/${attendanceId}`, { method: 'DELETE' }),

  // Stats
  getAttendanceStats: (date = null, floorId = '') => {
    const q = new URLSearchParams();
    if (date) q.append('date', date);
    if (floorId) q.append('floorId', floorId);
    const qs = q.toString();
    return fetchApi(`/attendance/stats${qs ? '?' + qs : ''}`);
  },

  // Realtime (existing — kept for compatibility)
  getRealtimeEmployees: (params = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.append('search', params.search);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 10);
    return fetchApi(`/attendance/realtime-employees${q.toString() ? '?' + q.toString() : ''}`);
  },

  markAttendance: (data) => fetchApi('/attendance/mark', { method: 'POST', body: JSON.stringify(data) }),

  getAttendanceReport: (startDate, endDate) => {
    const q = new URLSearchParams({ startDate, endDate }).toString();
    return fetchApi(`/attendance/report?${q}`);
  },
};

// Courier Credential API
export const courierCredentialApi = {
  getAllCredentials: () => fetchApi('/courier-credential'),
  getCredentialById: (id) => fetchApi(`/courier-credential/${id}`),
  getCredentialByCampaign: (campaignName) => fetchApi(`/courier-credential/campaign/${encodeURIComponent(campaignName)}`),
  getCredentialsByStatus: (status) => fetchApi(`/courier-credential/status/${status}`),
  getCredentialsByCourierType: (courierType) => fetchApi(`/courier-credential/courier-type/${encodeURIComponent(courierType)}`),
  createCredential: (data) => fetchApi('/courier-credential', { method: 'POST', body: JSON.stringify(data) }),
  updateCredential: (id, data) => fetchApi(`/courier-credential/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleCredentialStatus: (id) => fetchApi(`/courier-credential/${id}/toggle-status`, { method: 'PATCH' }),
  deleteCredential: (id) => fetchApi(`/courier-credential/${id}`, { method: 'DELETE' }),
};

// RBAC API
export const rbacApi = {
  getAllRoles: () => fetchApi('/roles'),
  getRoleById: (roleId) => fetchApi(`/roles/${roleId}`),
  getRoleByName: (roleName) => fetchApi(`/roles/name/${encodeURIComponent(roleName)}`),
  createRole: (roleData) => fetchApi('/roles', { method: 'POST', body: JSON.stringify(roleData) }),
  updateRole: (roleId, roleData) => fetchApi(`/roles/${roleId}`, { method: 'PUT', body: JSON.stringify(roleData) }),
  deleteRole: (roleId) => fetchApi(`/roles/${roleId}`, { method: 'DELETE' }),
  getAllPermissions: () => fetchApi('/roles/permissions/all'),
  addPermissionToRole: (roleId, permission) => fetchApi(`/roles/${roleId}/permissions`, {
    method: 'POST',
    body: JSON.stringify({ permission }),
  }),
  removePermissionFromRole: (roleId, permissionName) => fetchApi(`/roles/${roleId}/permissions/${encodeURIComponent(permissionName)}`, { method: 'DELETE' }),
  checkRolePermission: (roleId, permissionName) => fetchApi(`/roles/${roleId}/permissions/${encodeURIComponent(permissionName)}/check`),
  initializeDefaultRoles: () => fetchApi('/roles/initialize', { method: 'POST' }),
  getPermissionsByDesignation: (designation) => fetchApi(`/user-permissions/designation/${encodeURIComponent(designation)}`),
  assignPermissionsToDesignation: (designation, permissions) => fetchApi(`/user-permissions/designation/${designation}`, {
    method: 'POST',
    body: JSON.stringify({ permissions }),
  }),
  updateDesignationPermissions: (designation, permissions) => fetchApi(`/user-permissions/designation/${encodeURIComponent(designation)}`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  }),
  assignRoleToUser: (userId, roleId) => fetchApi(`/employees/${userId}/role`, { method: 'POST', body: JSON.stringify({ roleId }) }),
  getUserPermissions: (userId) => fetchApi(`/employees/${userId}/permissions`),
  checkUserPermission: (userId, permission) => fetchApi(`/employees/${userId}/permissions/${encodeURIComponent(permission)}/check`),
  getUserWithRole: (userId) => fetchApi(`/employees/${userId}`),
  getAllUsersWithRoles: () => fetchApi('/employees'),
  updateUserStatus: (userId, isActive) => fetchApi(`/employees/${userId}/status`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
  deleteUser: (userId) => fetchApi(`/employees/${userId}`, { method: 'DELETE' }),
  assignRoleToMultipleUsers: (userIds, roleId) => fetchApi('/employees/batch/role', { method: 'POST', body: JSON.stringify({ userIds, roleId }) }),
  addPermissionToMultipleRoles: (roleIds, permission) => fetchApi('/roles/batch/permissions', { method: 'POST', body: JSON.stringify({ roleIds, permission }) }),
  bulkUpdateUserStatus: (userIds, isActive) => fetchApi('/employees/batch/status', { method: 'POST', body: JSON.stringify({ userIds, isActive }) }),
  getRoleTemplates: () => fetchApi('/roles/templates'),
  createRoleFromTemplate: (templateName, customData = {}) => fetchApi('/roles/from-template', { method: 'POST', body: JSON.stringify({ templateName, ...customData }) }),
  getRoleUsageStats: () => fetchApi('/roles/analytics/usage'),
  getPermissionUsageStats: () => fetchApi('/roles/analytics/permissions'),
  getUserAccessReport: (startDate, endDate) => {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', startDate);
    queryParams.append('endDate', endDate);
    return fetchApi(`/employees/access-report?${queryParams.toString()}`);
  },
  getAuditLog: (params = {}) => {
    const { userId, action, startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    if (userId) queryParams.append('userId', userId);
    if (action) queryParams.append('action', action);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    const queryString = queryParams.toString();
    return fetchApi(`/rbac/audit-log${queryString ? '?' + queryString : ''}`);
  },
  getPermissionGroups: () => fetchApi('/roles/permission-groups'),
  createPermissionGroup: (groupData) => fetchApi('/roles/permission-groups', { method: 'POST', body: JSON.stringify(groupData) }),
  updatePermissionGroup: (groupId, groupData) => fetchApi(`/roles/permission-groups/${groupId}`, { method: 'PUT', body: JSON.stringify(groupData) }),
  deletePermissionGroup: (groupId) => fetchApi(`/roles/permission-groups/${groupId}`, { method: 'DELETE' }),
};

// Role API
export const roleApi = {
  getAllRoles: () => fetchApi('/roles'),
  getRoleById: (id) => fetchApi(`/roles/${id}`),
  getRoleByName: (name) => fetchApi(`/roles/name/${encodeURIComponent(name)}`),
  createRole: (roleDTO) => fetchApi('/roles', { method: 'POST', body: JSON.stringify(roleDTO) }),
  updateRole: (id, roleDTO) => fetchApi(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(roleDTO) }),
  addPermissionToRole: (id, permission) => fetchApi(`/roles/${id}/permissions`, { method: 'POST', body: JSON.stringify({ permission }) }),
  removePermissionFromRole: (id, permission) => fetchApi(`/roles/${id}/permissions/${encodeURIComponent(permission)}`, { method: 'DELETE' }),
  deleteRole: (id) => fetchApi(`/roles/${id}`, { method: 'DELETE' }),
  getAllPermissions: () => fetchApi('/roles/permissions/all'),
  checkPermission: (id, permission) => fetchApi(`/roles/${id}/permissions/${encodeURIComponent(permission)}/check`),
  initializeDefaultRoles: () => fetchApi('/roles/initialize', { method: 'POST' }),
};

// Permissions API
export const permissionsApi = {
  getAllPermissions: () => fetchApi('/permissions'),
  getActivePermissions: () => fetchApi('/permissions/active'),
  getPermissionById: (id) => fetchApi(`/permissions/${id}`),
  getPermissionByCode: (code) => fetchApi(`/permissions/code/${encodeURIComponent(code)}`),
  getPermissionsByModule: (module) => fetchApi(`/permissions/module/${encodeURIComponent(module)}`),
  getPermissionsByCategory: (category) => fetchApi(`/permissions/category/${encodeURIComponent(category)}`),
  createPermission: (data) => fetchApi('/permissions', { method: 'POST', body: JSON.stringify(data) }),
  updatePermission: (id, data) => fetchApi(`/permissions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePermission: (id) => fetchApi(`/permissions/${id}`, { method: 'DELETE' }),
  togglePermissionStatus: (id) => fetchApi(`/permissions/${id}/toggle-status`, { method: 'PATCH' }),
  getEmployeePermissions: (employeeId) => fetchApi(`/permissions/employee/${employeeId}`),

  assignPermissions: async (employeeId, permissionIds, reason = '') => {
    try {
      const userInfo = getUserInfo();
      const response = await handleDirectFetch(`${BASE_URL}/permissions/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId,
          permissionIds,
          reason,
          assignedBy: userInfo?.name || 'Admin',
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (error) {
      console.error('Error assigning permissions:', error);
      throw error;
    }
  },

  assignPermissionsToEmployee: (data) => fetchApi('/permissions/assign', { method: 'POST', body: JSON.stringify(data) }),
  addPermissionsToEmployee: (employeeId, data) => fetchApi(`/permissions/employee/${employeeId}/add`, { method: 'POST', body: JSON.stringify(data) }),
  removePermissionsFromEmployee: (employeeId, data) => fetchApi(`/permissions/employee/${employeeId}/remove`, { method: 'POST', body: JSON.stringify(data) }),
  clearEmployeePermissions: (employeeId) => fetchApi(`/permissions/employee/${employeeId}/clear`, { method: 'DELETE' }),
  assignModuleToEmployee: (employeeId, module) => fetchApi(`/permissions/employee/${employeeId}/module/${encodeURIComponent(module)}`, { method: 'POST' }),
  removeModuleFromEmployee: (employeeId, module) => fetchApi(`/permissions/employee/${employeeId}/module/${encodeURIComponent(module)}`, { method: 'DELETE' }),
  checkEmployeePermission: (employeeId, permissionCode) => fetchApi(`/permissions/employee/${employeeId}/has/${encodeURIComponent(permissionCode)}`),
  checkEmployeeModuleAccess: (employeeId, module) => fetchApi(`/permissions/employee/${employeeId}/module/${encodeURIComponent(module)}/access`),
  bulkAssignPermissions: (data) => fetchApi('/permissions/bulk-assign', { method: 'POST', body: JSON.stringify(data) }),
  copyEmployeePermissions: (sourceEmployeeId, targetEmployeeId) => fetchApi('/permissions/copy', { method: 'POST', body: JSON.stringify({ sourceEmployeeId, targetEmployeeId }) }),
  copyPermissionsToMultiple: (sourceEmployeeId, targetEmployeeIds) => fetchApi('/permissions/copy-multiple', { method: 'POST', body: JSON.stringify({ sourceEmployeeId, targetEmployeeIds }) }),
  getAllModules: () => fetchApi('/permissions/modules'),
  getAllCategories: () => fetchApi('/permissions/categories'),
  getPermissionSummary: () => fetchApi('/permissions/summary'),
  searchPermissions: (query) => fetchApi(`/permissions/search?q=${encodeURIComponent(query)}`),
  getPermissionsByDesignation: (designationId) => fetchApi(`/designations/${designationId}/permissions`),
  assignPermissionsToDesignation: (designationId, permissions) => fetchApi(`/user-permissions/designation/${designationId}`, { method: 'POST', body: JSON.stringify({ permissions }) }),
  updateDesignationPermissions: (designationId, permissions) => fetchApi(`/user-permissions/designation/${designationId}`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
};
export const businessDashboardApi = {

  // ── KPI Cards ────────────────────────────────────────────────
  getKPI: (params = {}) => {
    const q = new URLSearchParams();
    if (params.type)       q.append('type',       params.type);
    if (params.startDate)  q.append('startDate',  params.startDate);
    if (params.endDate)    q.append('endDate',     params.endDate);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.teamLeadId) q.append('teamLeadId', params.teamLeadId);
    if (params.teamId)     q.append('teamId',     params.teamId);
    if (params.process)    q.append('process',    params.process);
    if (params.shift)      q.append('shift',      params.shift);
    if (params.status)     q.append('status',     params.status);
    if (params.source)     q.append('source',     params.source);
    return fetchApiDirect(`/dashboard/business/kpi?${q.toString()}`);
  },

  // ── Monthly Performance ──────────────────────────────────────
  getMonthlyPerformance: (params = {}) => {
    const q = new URLSearchParams();
    if (params.type)       q.append('type',       params.type);
    if (params.startDate)  q.append('startDate',  params.startDate);
    if (params.endDate)    q.append('endDate',     params.endDate);
    if (params.teamLeadId) q.append('teamLeadId', params.teamLeadId);
    if (params.teamId)     q.append('teamId',     params.teamId);
    if (params.source)     q.append('source',     params.source);
    return fetchApiDirect(`/dashboard/business/monthly-performance?${q.toString()}`);
  },

  // ── Sales Funnel ─────────────────────────────────────────────
  getFunnel: (params = {}) => {
    const q = new URLSearchParams();
    if (params.type)       q.append('type',       params.type);
    if (params.startDate)  q.append('startDate',  params.startDate);
    if (params.endDate)    q.append('endDate',     params.endDate);
    if (params.teamLeadId) q.append('teamLeadId', params.teamLeadId);
    if (params.teamId)     q.append('teamId',     params.teamId);
    if (params.source)     q.append('source',     params.source);
    return fetchApiDirect(`/dashboard/business/funnel?${q.toString()}`);
  },

  // ── Managers Focus ───────────────────────────────────────────
  getManagersFocus: (params = {}) => {
    const q = new URLSearchParams();
    if (params.type)       q.append('type',       params.type);
    if (params.startDate)  q.append('startDate',  params.startDate);
    if (params.endDate)    q.append('endDate',     params.endDate);
    if (params.teamLeadId) q.append('teamLeadId', params.teamLeadId);
    if (params.teamId)     q.append('teamId',     params.teamId);
    if (params.source)     q.append('source',     params.source);
    return fetchApiDirect(`/dashboard/managers/focus?${q.toString()}`);
  },

  // ── Team Insights ────────────────────────────────────────────
  getTeamInsights: (managerId, params = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.append('type', params.type);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApiDirect(`/dashboard/managers/${managerId}/team-insights?${q.toString()}`);
  },

  // ── All Managers ─────────────────────────────────────────────
  getAllManagers: (params = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.append('type', params.type);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 10);
    return fetchApiDirect(`/dashboard/managers/all?${q.toString()}`);
  },

  // ── Team Lead Agents ─────────────────────────────────────────
  getTeamLeadAgents: (teamLeadId, params = {}) => {
    const q = new URLSearchParams();
    if (params.type) q.append('type', params.type);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 18);
    return fetchApiDirect(`/dashboard/teamleads/${teamLeadId}/agents?${q.toString()}`);
  },

  // ── APR Report ───────────────────────────────────────────────
  getAPRReport: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 20);
    return fetchApiDirect(`/dashboard/apr/report?${q.toString()}`);
  },

  // ── Lead Summary ─────────────────────────────────────────────
  getLeadSummary: (params = {}) => {
    const q = new URLSearchParams();
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApiDirect(`/dashboard/leads/summary?${q.toString()}`);
  },

  // ── Lead Status Breakdown ────────────────────────────────────
  getLeadStatusBreakdown: (params = {}) => {
    const q = new URLSearchParams();
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApiDirect(`/dashboard/leads/status-breakdown?${q.toString()}`);
  },

  // ── Lead Conversion Summary ──────────────────────────────────
  getLeadConversionSummary: (params = {}) => {
    const q = new URLSearchParams();
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApiDirect(`/dashboard/leads/conversion-summary?${q.toString()}`);
  },

  // ── Analytics Overview ───────────────────────────────────────
  getAnalyticsOverview: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.floorId) q.append('floorId', params.floorId);
    return fetchApiDirect(`/dashboard/analytics/overview?${q.toString()}`);
  },

  // ── Employee Performance ─────────────────────────────────────
  getEmployeePerformance: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.floorId) q.append('floorId', params.floorId);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 10);
    return fetchApiDirect(`/dashboard/analytics/employee-performance?${q.toString()}`);
  },

  // ── Grand Total ──────────────────────────────────────────────
  getGrandTotal: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.floorId) q.append('floorId', params.floorId);
    return fetchApiDirect(`/dashboard/analytics/grand-total?${q.toString()}`);
  },

  // ── Floor Analytics ──────────────────────────────────────────
  getFloorAnalyticsOverview: (params = {}) => {
    const q = new URLSearchParams();
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApiDirect(`/dashboard/analytics/floor-overview?${q.toString()}`);
  },

  getFloorEmployeePerformance: (params = {}) => {
    const q = new URLSearchParams();
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 20);
    return fetchApiDirect(`/dashboard/analytics/floor-employee-performance?${q.toString()}`);
  },

  // ── Top Performers ───────────────────────────────────────────
  getTopPerformers: (params = {}) => {
    const q = new URLSearchParams();
    q.append('limit', params.limit ?? 5);
    if (params.metric) q.append('metric', params.metric);
    if (params.floorId) q.append('floorId', params.floorId);
    if (params.teamId) q.append('teamId', params.teamId);
    if (params.employeeId) q.append('employeeId', params.employeeId);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    return fetchApiDirect(`/dashboard/stats/top-performers?${q.toString()}`);
  },

  // ── AHT ─────────────────────────────────────────────────────
  getAHTBuckets: (params = {}) => {
    const q = new URLSearchParams();
    if (params.campaignType) q.append('campaignType', params.campaignType);
    if (params.teamId) q.append('teamId', params.teamId);
    return fetchApiDirect(`/dashboard/campaign/aht-buckets?${q.toString()}`);
  },

  getAHTCampaignWise: (params = {}) => {
    const q = new URLSearchParams();
    if (params.leadType) q.append('leadType', params.leadType);
    return fetchApiDirect(`/dashboard/campaign/aht-campaign-wise?${q.toString()}`);
  },

  getAHTCampaignSummary: (params = {}) => {
    const q = new URLSearchParams();
    if (params.leadType) q.append('leadType', params.leadType);
    return fetchApiDirect(`/dashboard/campaign/aht-summary?${q.toString()}`);
  },

  getAHTEmployees: (params = {}) => {
    const q = new URLSearchParams();
    if (params.leadType) q.append('leadType', params.leadType);
    if (params.teamId) q.append('teamId', params.teamId);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 20);
    return fetchApiDirect(`/dashboard/campaign/aht-employees?${q.toString()}`);
  },

  getAHTDispositions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.leadType) q.append('leadType', params.leadType);
    if (params.teamId) q.append('teamId', params.teamId);
    return fetchApiDirect(`/dashboard/campaign/aht-dispositions?${q.toString()}`);
  },

  getAHTDispositionsDrilldown: (params = {}) => {
    const q = new URLSearchParams();
    if (params.dispositionType) q.append('dispositionType', params.dispositionType);
    if (params.timeBucket) q.append('timeBucket', params.timeBucket);
    if (params.leadType) q.append('leadType', params.leadType);
    if (params.search) q.append('search', params.search);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 9);
    return fetchApiDirect(`/dashboard/campaign/aht-dispositions/drilldown?${q.toString()}`);
  },

  getCallRecording: (callLogId) =>
    fetchApiDirect(`/dashboard/campaign/aht-dispositions/recording/${callLogId}`),

  // ── Team Order History ───────────────────────────────────────
  getTeamOrderHistory: (teamId, params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.status) q.append('status', params.status);
    q.append('page', params.page ?? 0);
    q.append('size', params.size ?? 20);
    return fetchApiDirect(`/dashboard/team/${teamId}/order-history?${q.toString()}`);
  },

  // ── Export ───────────────────────────────────────────────────
  exportDashboard: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.dashboardType) q.append('dashboardType', params.dashboardType);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    const response = await handleDirectFetch(
      `${BASE_URL}/dashboard/export?${q.toString()}`, {}
    );
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_${params.dashboardType || 'export'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { success: true };
  },
};
// Session API
export const sessionApi = {

  heartbeat: (employeeId) => fetchApi('/session/heartbeat', {
    method: 'POST',
    body: JSON.stringify({ employeeId }),
  }),

  markOnline: (employeeId) => fetchApi(`/session/online/${employeeId}`, {
    method: 'POST',
  }),

  markOffline: (employeeId) => fetchApi(`/session/offline/${employeeId}`, {
    method: 'POST',
  }),

  getOnlineAgents: () => fetchApi('/session/online-agents'),

  triggerAutoAssign: () => fetchApi('/session/trigger-assign', { method: 'POST' }),

};
window.addEventListener('beforeunload', () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user?.id) {
    // beacon use karo — async nahi chalega beforeunload mein
    navigator.sendBeacon(
      `${BASE_URL}/session/offline/${user.id}`,
      JSON.stringify({})
    );
  }
});
// ============================================================
// 📦 STOCK MANAGEMENT APIs — api.js ke END me paste karo
// (default export se pehle). fetchApi/handleDirectFetch same
// file ke use hote hain, isliye token refresh free me milega.
// ============================================================

// Warehouse API
export const warehouseApi = {
  getAllWarehouses: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/warehouses${q ? '?' + q : ''}`);
  },
  getWarehouseById: (id) => fetchApi(`/warehouses/${id}`),
  getActiveWarehouses: async () => {
    const data = await fetchApi('/warehouses');
    const list = Array.isArray(data)
      ? data
      : data?.centers || data?.warehouses || data?.content || data?.data || [];
    return Array.isArray(list) ? list : [];
  },
  // 🔧 FIXED: was hitting `/stock-in/{id}/repair-product-links` (POST-only repair
  // endpoint, wrong resource entirely) instead of the actual inventory-by-warehouse
  // endpoint. That's why "Transfer From" showed 0 SKUs / no products.
  getWarehouseStock: (id) => fetchApi(`/inventory/warehouse/${id}`),
  createWarehouse: (data) => fetchApi('/warehouses', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateWarehouse: (id, data) => fetchApi(`/warehouses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteWarehouse: (id) => fetchApi(`/warehouses/${id}`, { method: 'DELETE' }),
};

// Vendor API (multi-step onboarding)
export const vendorApi = {
  // ⚠️ createVendor / updateVendor neeche define hain. Yahan inke duplicate
  //    keys the jinhe JS silently drop kar deta tha (dead code).

  getAllVendors: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/vendors${q ? '?' + q : ''}`);
  },
  getVendorById: (id) => fetchApi(`/vendors/${id}`),
  getActiveVendors: async () => {
    const data = await request('/vendors');
    const list = data.content || data.data || data || [];
    return Array.isArray(list) ? list.filter(v => v.active !== false) : list;
  },
  searchVendors: (searchTerm) =>
    request(`/vendors?search=${encodeURIComponent(searchTerm || '')}`),

  getVendor: (id) => request(`/vendors/${id}`),
  createVendor: (data) => fetchApi('/vendors', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateVendor: (id, data) => fetchApi(`/vendors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  // 3-step onboarding: Company → License → Address
  onboardStep1: (data) => fetchApi('/vendors/onboard/step1', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  onboardStep2: (data) => fetchApi('/vendors/onboard/step2', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  onboardStep3: (data) => fetchApi('/vendors/onboard/step3', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // ⚠️ ASSUMED ENDPOINT — B2BPartners.js ise call karta tha lekin ye define
  //    hi nahi thi (toggle hamesha silently fail hota tha). Baaki toggle
  //    endpoints ke pattern (/{resource}/{id}/toggle) se match kiya hai.
  //    Backend se confirm karein.
  toggleVendorActive: (id) => fetchApi(`/vendors/${id}/toggle`, { method: 'PATCH' }),
};

// Stock Inventory API
export const stockInventoryApi = {
  getByWarehouse: (warehouseId) => fetchApi(`/inventory/warehouse/${warehouseId}`),

  getByWarehousePaginated: (warehouseId, params = {}) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    if (params.search) q.append('search', params.search);
    const qs = q.toString();
    return fetchApi(`/inventory/warehouse/${warehouseId}/paginated${qs ? '?' + qs : ''}`);
  },

  getLowStockList: () => fetchApi('/inventory/low-stock/list'),
  getLowStockCount: () => fetchApi('/inventory/low-stock/count'),
  getSummary: (warehouseId) => fetchApi(`/inventory/summary/${warehouseId}`),
  getItem: (warehouseId, productId) => fetchApi(`/inventory/${warehouseId}/${productId}`),
};

// Stock Transfer API
export const stockTransferApi = {
  getAllTransfers: (params = {}) => {
    const q = new URLSearchParams();
    if (params.searchTerm) q.append('searchTerm', params.searchTerm);
    if (params.sourceWarehouse && params.sourceWarehouse !== 'All')
      q.append('sourceWarehouse', params.sourceWarehouse);
    if (params.destinationWarehouse && params.destinationWarehouse !== 'All')
      q.append('destinationWarehouse', params.destinationWarehouse);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    const qs = q.toString();
    return fetchApi(`/transfers${qs ? '?' + qs : ''}`);
  },

  // ✅ YE ADD KARO — getAllTransfers ka alias hai
  searchTransfers: (params = {}) => {
    const q = new URLSearchParams();
    if (params.searchTerm) q.append('searchTerm', params.searchTerm);
    if (params.sourceWarehouse && params.sourceWarehouse !== 'All')
      q.append('sourceWarehouse', params.sourceWarehouse);
    if (params.destinationWarehouse && params.destinationWarehouse !== 'All')
      q.append('destinationWarehouse', params.destinationWarehouse);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    const qs = q.toString();
    return fetchApi(`/transfers${qs ? '?' + qs : ''}`);
  },

  downloadDeliveryChallanPdf: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/transfers/${id}/delivery-challan/pdf`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to download delivery challan: ${response.status}`);
    return response.blob();
  },

  // ✅ SIRF EK BAAR (duplicate hataya)
  downloadInvoicePdf: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/transfers/${id}/invoice/pdf`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to download invoice: ${response.status}`);
    return response.blob();
  },

  // ✅ SIRF EK BAAR (duplicate hataya)
  downloadEwayBillPdf: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/transfers/${id}/eway-bill/pdf`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to download e-way bill: ${response.status}`);
    return response.blob();
  },

  getTransferById: (id) => fetchApi(`/transfers/${id}`),
  getTransfersByStatus: (status) => fetchApi(`/transfers/status/${status}`),
  getTodayTransfers: () => fetchApi('/transfers/today/list'),

  createTransfer: (data) => fetchApi('/transfers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  approveTransfer: (id) => fetchApi(`/transfers/${id}/approve`, { method: 'PATCH' }),

  receiveTransfer: (id, data) => fetchApi(`/transfers/${id}/receive`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  completeTransfer: (id) => fetchApi(`/transfers/${id}/complete`, { method: 'PATCH' }),

  exportTransfersPdf: async ({ search, startDate, endDate } = {}) => {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    const response = await handleDirectFetch(`${BASE_URL}/transfers/export/pdf${q.toString() ? '?' + q.toString() : ''}`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to export PDF: ${response.status}`);
    return response.blob();
  },

  exportTransfersExcel: async ({ search, startDate, endDate } = {}) => {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    const response = await handleDirectFetch(`${BASE_URL}/transfers/export/excel${q.toString() ? '?' + q.toString() : ''}`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to export Excel: ${response.status}`);
    return response.blob();
  },
};
export const transferApi = {
  // POST /api/transfers
  createTransfer: (payload) =>
    request('/transfers', { method: 'POST', body: JSON.stringify(payload) }),

  // GET /api/transfers?search=&warehouseId=&startDate=&endDate=&page=&size=
  searchTransfers: async ({ searchTerm, source, destination, startDate, endDate, page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('page', page);
    params.append('size', size);
    // ⚠️ doc me sirf ek warehouseId filter hai — source/destination alag se filter nahi kar sakte
    // server-side. From/To dropdown filters ko client-side hi apply karna padega abhi.
    return request(`/transfers?${params.toString()}`);
  },

  getTransfer: (id) => request(`/transfers/${id}`),

  // GET /api/transfers/pending-queue — IN_TRANSIT transfers, "Receive Transfer" screen ke liye
  getPendingQueue: () => request('/transfers/pending-queue'),

  // POST /api/transfers/{id}/receive
  receiveTransfer: (id, payload) =>
    request(`/transfers/${id}/receive`, { method: 'POST', body: JSON.stringify(payload) }),

  downloadDeliveryChallanPdf: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/transfers/${id}/delivery-challan/pdf`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to download delivery challan: ${response.status}`);
    return response.blob();
  },

  downloadInvoicePdf: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/transfers/${id}/invoice/pdf`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to download invoice: ${response.status}`);
    return response.blob();
  },

  downloadEwayBillPdf: async (id) => {
    const response = await handleDirectFetch(`${BASE_URL}/transfers/${id}/eway-bill/pdf`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to download e-way bill: ${response.status}`);
    return response.blob();
  },

  exportTransfersPdf: async ({ search, startDate, endDate } = {}) => {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    const response = await handleDirectFetch(`${BASE_URL}/transfers/export/pdf${q.toString() ? '?' + q.toString() : ''}`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to export PDF: ${response.status}`);
    return response.blob();
  },

  exportTransfersExcel: async ({ search, startDate, endDate } = {}) => {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);
    const response = await handleDirectFetch(`${BASE_URL}/transfers/export/excel${q.toString() ? '?' + q.toString() : ''}`, { method: 'GET' });
    if (!response.ok) throw new Error(`Failed to export Excel: ${response.status}`);
    return response.blob();
  },
};
// Stock In API
export const stockInApi = {
  exportStockInPdf: async ({ search, startDate, endDate } = {}) => {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);

    const response = await handleDirectFetch(
      `${BASE_URL}/stock-in/export/pdf${q.toString() ? '?' + q.toString() : ''}`,
      { method: 'GET' }
    );
    if (!response.ok) {
      throw new Error(`Failed to export PDF: ${response.status}`);
    }
    return response.blob();
  },

  // ✅ NEW — Bulk Excel export of Stock-In list
  exportStockInExcel: async ({ search, startDate, endDate } = {}) => {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (startDate) q.append('startDate', startDate);
    if (endDate) q.append('endDate', endDate);

    const response = await handleDirectFetch(
      `${BASE_URL}/stock-in/export/excel${q.toString() ? '?' + q.toString() : ''}`,
      { method: 'GET' }
    );
    if (!response.ok) {
      throw new Error(`Failed to export Excel: ${response.status}`);
    }
    return response.blob();
  },
  createStockIn: (payload) =>
    request('/stock-in', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getByWarehouse: (warehouseId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/stock-in/warehouse/${warehouseId}${q ? '?' + q : ''}`);
  },

  getStockInById: (id) => fetchApi(`/stock-in/${id}`),
  getByStatus: (status) => fetchApi(`/stock-in/status/${status}`),
  getTodaySummary: () => fetchApi('/stock-in/today/summary'),

  searchStockIn: async ({ searchTerm, warehouseId, vendorId, status, startDate, endDate, page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('page', page);
    params.append('size', size);
    if (warehouseId && warehouseId !== 'All') params.append('warehouseId', warehouseId);
    if (vendorId && vendorId !== 'All') params.append('vendorId', vendorId);
    if (status) params.append('status', status);
    return request(`/stock-in?${params.toString()}`);
  },
  getStockIn: (id) => request(`/stock-in/${id}`),

  updateStockIn: (id, payload) =>
    request(`/stock-in/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),

  discardStockIn: (id) => request(`/stock-in/${id}`, { method: 'DELETE' }),

  // ⚠️ Doc me koi /stock-in/summary endpoint nahi hai — abhi ye 404 dega.
  // Component already .catch(() => null) se ise handle kar raha hai, so list load nahi rukega,
  // lekin cards (Received/Pending/Value) fallback pe entries se calculate honge.

  saveDraft: (id, data) => fetchApi(`/stock-in/${id}/draft`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  completeStockIn: (id) => fetchApi(`/stock-in/${id}/complete`, { method: 'PATCH' }),
};

// Stock Return API
export const stockReturnApi = {
  getAllReturns: (params = {}) => {
    const q = new URLSearchParams();
    if (params.searchTerm) q.append('searchTerm', params.searchTerm);
    if (params.returnId) q.append('returnId', params.returnId);
    if (params.courier && params.courier !== 'All') q.append('courier', params.courier);
    if (params.warehouse && params.warehouse !== 'All') q.append('warehouse', params.warehouse);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    const qs = q.toString();
    return fetchApi(`/returns${qs ? '?' + qs : ''}`);
  },

  // ✅ NEW — StockReturns.jsx yahi naam call karta hai; getAllReturns ko hi
  // wrap karke params ka naam match kar diya (search → searchTerm).
  searchReturns: ({ search, startDate, endDate, page, size } = {}) =>
    stockReturnApi.getAllReturns({ searchTerm: search, startDate, endDate, page, size }),

  getReturnById: (id) => fetchApi(`/returns/${id}`),
  // ✅ NEW — alias, StockReturns.jsx isi naam se ek single return fetch karta hai
  getReturn: (id) => fetchApi(`/returns/${id}`),

  getReturnsByStatus: (status) => fetchApi(`/returns/status/${status}`),

  getPendingReturns: () => fetchApi('/returns/pending/list'),
  // ✅ NEW — alias, StockReturns.jsx isi naam se pending queue fetch karta hai
  getPendingQueue: () => fetchApi('/returns/pending/list'),

  getReturnsSummary: () => fetchApi('/returns/summary'),

  createReturn: (data) => fetchApi('/returns', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  inspectReturn: (id, data) => fetchApi(`/returns/${id}/inspect`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  restockReturn: (id, data) => fetchApi(`/returns/${id}/restock`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  rejectReturn: (id, data) => fetchApi(`/returns/${id}/reject`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
};
// ============================================================
// ✅ request() — fetchApi ke barabar: Authorization header +
//    silent 401 refresh. Pehle ye dono missing the, isliye
//    stock-in / transfers / vendors / stock-dashboard ke saare
//    calls bina token ke jaate the aur 401 pe fail ho jaate the.
// ============================================================
async function request(path, options = {}) {
  // ⚠️ `headers` ko alag nikalna zaroori hai — pehle `...options` baad me
  // spread hota tha jo merge kiye hue headers ko poora overwrite kar deta tha.
  const { headers: optHeaders, ...restOptions } = options;

  const buildConfig = (token) => ({
    ...restOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...optHeaders,
    },
    mode: 'cors',
  });

  const makeRequest = (token) => fetch(`${BASE_URL}${path}`, buildConfig(token));

  let res = await makeRequest(localStorage.getItem(AUTH_TOKEN_KEY));

  // 401 — fetchApi jaisa hi silent refresh + single retry
  if (res.status === 401) {
    if (isRefreshing) {
      // Koi aur refresh chala raha hai — queue mein wait karo
      const newToken = await new Promise((resolve, reject) => {
        addRefreshSubscriber(resolve);
        setTimeout(
          () => reject(new Error('Session expired. Please login again.')),
          30000,
        );
      });
      res = await makeRequest(newToken);
    } else {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        onRefreshed(newToken);
        res = await makeRequest(newToken);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        console.error('Token refresh failed:', refreshError);
        throw new Error('Session expired. Please login again.');
      }
    }
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const errBody = await res.json();
      message = errBody.message || errBody.error || message;
    } catch (_) { }
    throw new Error(message);
  }

  const contentType = res.headers.get('content-type') || '';
  return contentType.includes('application/json') ? res.json() : res;
}
// Cache warehouses briefly so we don't refetch on every export click
let _warehouseCache = null;
async function resolveWarehouseId(name) {
  if (!name || name === 'All') return null;
  if (!_warehouseCache) {
    _warehouseCache = await request('/warehouses');
  }
  const list = Array.isArray(_warehouseCache) ? _warehouseCache : (_warehouseCache.content || []);
  const match = list.find(w => w.name === name || w.code === name);
  return match ? match.id : null;
}
// Stock Ledger API
// 🔧 FIXED: backend actually serves this module at /api/stock-ledger (list) and
// /api/stock-ledger/export-legacy (export) — confirmed via curl. The old code
// hit /ledger and /ledger/export, which don't exist on this backend, so every
// call here was 404ing silently (list always empty, export always failing).
// There was also a duplicate `exportLedger` key in the old object literal —
// JS silently kept only the second one, so the first (dead) version is removed.
export const stockLedgerApi = {
  getByWarehouse: (warehouseId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/stock-ledger/warehouse/${warehouseId}${q ? '?' + q : ''}`);
  },

  getByProduct: (productId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetchApi(`/stock-ledger/product/${productId}${q ? '?' + q : ''}`);
  },

  // Advanced filters — saara filtering server-side. quickRange backend expects
  // UPPERCASE: 'TODAY' | '7D' | '30D' (confirmed via curl examples).
  searchLedger: (params = {}) => {
    const q = new URLSearchParams();
    if (params.searchTerm) q.append('searchTerm', params.searchTerm);
    if (params.warehouseId) q.append('warehouseId', params.warehouseId);
    if (params.transactionType && params.transactionType !== 'All')
      q.append('transactionType', params.transactionType);
    if (params.batchNumber) q.append('batchNumber', params.batchNumber);
    if (params.quickRange) q.append('quickRange', String(params.quickRange).toUpperCase()); // TODAY | 7D | 30D
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    const qs = q.toString();
    return fetchApi(`/stock-ledger${qs ? '?' + qs : ''}`);
  },
  getByDateRange: (startDate, endDate) => {
    const q = new URLSearchParams({ startDate, endDate }).toString();
    return fetchApi(`/stock-ledger/date-range?${q}`);
  },

  // ⚠️ UNCONFIRMED — no curl example given for a summary endpoint. Guessed as
  // /stock-ledger/summary to match the corrected base path; verify with backend
  // and update if it 404s (component already has a fallback that computes
  // totals from the current page if this call fails).
  // 🔧 FIXED: was `new URLSearchParams(params).toString()` — when params has
  // keys with an `undefined` value (very common here, since buildParams() in
  // StockLedger.jsx sets warehouseId/transactionType/quickRange to `undefined`
  // when "not selected"), URLSearchParams does NOT drop them — it stringifies
  // them into the literal text "undefined" (e.g. `warehouseId=undefined`).
  // The backend then fails to parse that as an int/enum and returns 400.
  // Now builds the query selectively, same pattern as searchLedger.
  getLedgerSummary: (params = {}) => {
    const q = new URLSearchParams();
    if (params.searchTerm) q.append('searchTerm', params.searchTerm);
    if (params.warehouseId) q.append('warehouseId', params.warehouseId);
    if (params.transactionType && params.transactionType !== 'All')
      q.append('transactionType', params.transactionType);
    if (params.batchNumber) q.append('batchNumber', params.batchNumber);
    if (params.quickRange) q.append('quickRange', String(params.quickRange).toUpperCase());
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    const qs = q.toString();
    return fetchApi(`/stock-ledger/summary${qs ? '?' + qs : ''}`);
  },

  // ✅ Excel export — blob download. PDF isn't supported by this backend.
  exportLedger: async (params = {}) => {
    if (params.format === 'pdf') {
      throw new Error("PDF export isn't supported by the backend yet — only Excel export is available.");
    }

    const q = new URLSearchParams();
    if (params.warehouseId) q.append('warehouseId', params.warehouseId);
    if (params.transactionType && params.transactionType !== 'All')
      q.append('transactionType', params.transactionType);
    if (params.quickRange) q.append('quickRange', String(params.quickRange).toUpperCase());
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);

    const response = await handleDirectFetch(`${BASE_URL}/stock-ledger/export-legacy?${q.toString()}`, {});
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Export failed: ${response.status}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock_ledger_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { success: true };
  },
};

// Stock Dashboard API
export const stockDashboardApi = {
  // GET /api/inventory/dashboard — used for quick-stats cards
  getQuickStats: async () => {
    const data = await request('/inventory/dashboard');
    return data.quickStats || data;
  },

  // GET /api/inventory/dashboard — used for warehouse summary table
  getWarehouseHealth: async () => {
    const data = await request('/inventory/dashboard');
    return data.warehouseHealth || data.warehouses || data;
  },

  // GET /api/inventory/dashboard — used for "Today's Summary" bars
  getTodaySummary: async () => {
    const data = await request('/inventory/dashboard');
    return data.todaySummary || data;
  },

  // GET /api/stock-ledger?quickRange=... — used for Recent Activity table
  getRecentActivity: async ({ period = 'today', limit = 10 } = {}) => {
    const rangeMap = { today: 'TODAY', week: '7D', month: '30D' };
    const quickRange = rangeMap[period] || 'TODAY';
    const data = await request(`/stock-ledger?quickRange=${quickRange}&page=0&size=${limit}`);
    return data.content || data.activities || data;
  },
};
// ============================================================
// 🏥 CENTERS API — Centers.jsx ke liye
// ============================================================
export const centersApi = {
  // GET /api/centers/dashboard/summary — top 4 stat cards
  getDashboardSummary: () => fetchApi('/centers/dashboard/summary'),

  // GET /api/centers?search=&status=&page=&size=
  getAllCenters: (params = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.append('search', params.search);
    if (params.status && params.status !== 'All')
      q.append('status', params.status.toUpperCase()); // 'Active' -> 'ACTIVE'
    if (params.page !== undefined) q.append('page', params.page);
    if (params.size !== undefined) q.append('size', params.size);
    const qs = q.toString();
    return fetchApi(`/centers${qs ? '?' + qs : ''}`);
  },

  // GET /api/centers/{id}
  getCenterById: (id) => fetchApi(`/centers/${id}`),

  // POST /api/centers
  createCenter: (data) => fetchApi('/centers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // PUT /api/centers/{id} — edit + metrics update dono ke liye
  updateCenter: (id, data) => fetchApi(`/centers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // PATCH /api/centers/{id}/status — {"status": "ACTIVE" | "INACTIVE"}
  updateCenterStatus: (id, status) => fetchApi(`/centers/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),

  // DELETE /api/centers/{id}
  deleteCenter: (id) => fetchApi(`/centers/${id}`, { method: 'DELETE' }),

  // Employee ko centre assign karna — existing employees endpoint se
  assignEmployeeToCenter: (employeeId, centerId) =>
    fetchApi(`/employees/${employeeId}`, {
      method: 'PUT',
      body: JSON.stringify({ centerId }),
    }),
};
export const samplesApi = {
  getAll: (page = 0, size = 1000) =>
    fetchApi(`/samples?page=${page}&size=${size}`),

  create: (data) => fetchApi('/samples', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  getSummary: () => fetchApi('/samples/summary'),
};
// ============================================================
// 💬 WHATSAPP API — paste this into api.js, BEFORE `export default {...}`
// Uses the same fetchApi() helper as everything else in this file,
// so auth token + silent 401-refresh are already handled for free.
// ============================================================
const normWA = (p) => {
  if (!p) return '';
  const d = String(p).replace(/[^0-9]/g, '');
  return d.length === 10 ? '91' + d : d;
};

export const whatsappApi = {
  // Generic — send any message type via the messageType field.
  sendMessage: (payload) => fetchApi('/whatsapp/send', {
    method: 'POST',
    body: JSON.stringify({ ...payload, to: normWA(payload?.to) }),
  }),
  getConversation: (phoneNumber) =>
    fetchApi(`/whatsapp/conversation/${encodeURIComponent(normWA(phoneNumber))}`),
  uploadMedia: async (file, type) => {
    // type: 'image' | 'video' | 'audio' | 'document' | 'sticker'
    const formData = new FormData();
    formData.append('file', file);

    const response = await handleDirectFetch(
      `${BASE_URL}/whatsapp/upload-media?type=${encodeURIComponent(type)}`,
      { method: 'POST', body: formData }
    );
    return handleResponse(response);
  },

  sendText: (to, message) =>
    fetchApi(`/whatsapp/send/text?to=${encodeURIComponent(normWA(to))}&message=${encodeURIComponent(message)}`, {
      method: 'POST',
    }),

  sendImage: (to, imageUrl, caption = '') =>
    fetchApi(`/whatsapp/send/image?to=${encodeURIComponent(normWA(to))}&imageUrl=${encodeURIComponent(imageUrl)}&caption=${encodeURIComponent(caption)}`, {
      method: 'POST',
    }),

  sendVideo: (to, videoUrl, caption = '') =>
    fetchApi(`/whatsapp/send/video?to=${encodeURIComponent(normWA(to))}&videoUrl=${encodeURIComponent(videoUrl)}&caption=${encodeURIComponent(caption)}`, {
      method: 'POST',
    }),

  sendAudio: (to, audioUrl) =>
    fetchApi(`/whatsapp/send/audio?to=${encodeURIComponent(normWA(to))}&audioUrl=${encodeURIComponent(audioUrl)}`, {
      method: 'POST',
    }),

  sendDocument: (to, documentUrl, filename = '') =>
    fetchApi(`/whatsapp/send/document?to=${encodeURIComponent(normWA(to))}&documentUrl=${encodeURIComponent(documentUrl)}&filename=${encodeURIComponent(filename)}`, {
      method: 'POST',
    }),

  sendSticker: (to, stickerUrl) =>
    fetchApi(`/whatsapp/send/sticker?to=${encodeURIComponent(normWA(to))}&stickerUrl=${encodeURIComponent(stickerUrl)}`, {
      method: 'POST',
    }),

  sendLocation: (to, latitude, longitude, name = '', address = '') =>
    fetchApi(`/whatsapp/send/location?to=${encodeURIComponent(normWA(to))}&latitude=${latitude}&longitude=${longitude}&name=${encodeURIComponent(name)}&address=${encodeURIComponent(address)}`, {
      method: 'POST',
    }),

  sendButtons: (to, message, buttonLabels) => fetchApi('/whatsapp/send/buttons', {
    method: 'POST',
    body: JSON.stringify({ to: normWA(to), message, buttonLabels }),
  }),

  sendList: (to, message, listButtonText, listOptions) => fetchApi('/whatsapp/send/list', {
    method: 'POST',
    body: JSON.stringify({ to: normWA(to), message, listButtonText, listOptions }),
  }),

  sendContact: (to, name, phone) =>
    fetchApi(`/whatsapp/send/contact?to=${encodeURIComponent(normWA(to))}&name=${encodeURIComponent(name)}&phone=${encodeURIComponent(phone)}`, {
      method: 'POST',
    }),

  sendReaction: (to, messageId, emoji) =>
    fetchApi(`/whatsapp/send/reaction?to=${encodeURIComponent(normWA(to))}&messageId=${encodeURIComponent(messageId)}&emoji=${encodeURIComponent(emoji)}`, {
      method: 'POST',
    }),

  sendTemplate: (to, templateName = 'hello_world', templateLanguage = 'en_US', templateParams = []) =>
    fetchApi('/whatsapp/send/template', {
      method: 'POST',
      body: JSON.stringify({
        to: normWA(to),
        templateName: templateName || 'hello_world',
        templateLanguage: templateLanguage || 'en_US',
        languageCode: templateLanguage || 'en_US',
        templateParams: templateParams || [],
      }),
    }),



  markAsRead: (messageId) => fetchApi(`/whatsapp/read/${encodeURIComponent(messageId)}`, {
    method: 'POST',
  }),

  health: () => fetchApi('/whatsapp/health'),
};
export default {
  ordersApi,
  productsApi,
  leadsApi,
  partnersApi,
  purchaseOrdersApi,
  employeesApi,
  whatsappApi,
  floorsApi,
  verifiedOrdersApi,
  departmentsApi,
  teamsApi,
  designationsApi,
  dashboardApi,
  workflowApi,
  authHelpers,
  leadAPIApi,
  orderCampaignApi,
  leadDispositionApi,
  attendanceApi,
  courierCredentialApi,
  rbacApi,
  roleApi,
  permissionsApi,
  invoiceApi,
  companyProfileApi,
  businessDashboardApi,
  sessionApi,
  warehouseApi,
  vendorApi,
  stockInventoryApi,
  stockTransferApi,
  stockInApi,
  stockReturnApi,
  stockLedgerApi,
  stockDashboardApi,
  centersApi,samplesApi
};